import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';

const Building3DPreview = ({ config, onUpdateItemPosition, onDeleteItem, onCloneItem, onChangeItemSize }) => {
  const containerRef = useRef(null);
  const threeRef = useRef({
    scene: null,
    renderer: null,
    camera: null,
    controls: null,
    frameId: null,
    initialized: false,
    draggables: [], // Store draggable meshes with metadata
    raycaster: new THREE.Raycaster(),
    mouse: new THREE.Vector2(),
    dragState: null, // { mesh, itemType, itemId, wall, startX }
    selectionBox: null // Bounding box helper for selected item
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragInfo, setDragInfo] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // {id, type, wall, position, dimensions, itemType}
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [measurements, setMeasurements] = useState(null); // {leftDist, rightDist, width, height}
  const [showResizeMenu, setShowResizeMenu] = useState(false);
  
  // Available sizes for resize
  const doorSizes = {
    garageDoors: [
      { label: "8'x7'", width: 8, height: 7 },
      { label: "9'x7'", width: 9, height: 7 },
      { label: "10'x8'", width: 10, height: 8 },
      { label: "10'x10'", width: 10, height: 10 },
      { label: "12'x10'", width: 12, height: 10 },
      { label: "12'x12'", width: 12, height: 12 },
      { label: "14'x12'", width: 14, height: 12 },
      { label: "16'x14'", width: 16, height: 14 },
    ],
    walkDoors: [
      { label: "3'x6'8\"", width: 3, height: 6.67 },
      { label: "3'x7'", width: 3, height: 7 },
      { label: "3'6\"x7'", width: 3.5, height: 7 },
    ],
    windows: [
      { label: "2'x2'", width: 2, height: 2 },
      { label: "3'x2'", width: 3, height: 2 },
      { label: "3'x3'", width: 3, height: 3 },
      { label: "4'x3'", width: 4, height: 3 },
      { label: "4'x4'", width: 4, height: 4 },
    ]
  };

  // Config with defaults
  const width = config?.width || 20;
  const length = config?.length || 21;
  const legHeight = config?.legHeight || 8;
  const roofStyle = config?.roofStyle || 'regular';
  const roofPitch = config?.roofPitch || 3;
  const roofColor = config?.roofColor || '#C41E3A';
  const trimColor = config?.trimColor || '#FFFFFF';
  const wallColor = config?.sideColor || '#FFFFFF';
  const wainscotColor = config?.wainscotColor || 'none';
  const buildingStyle = config?.style || '';
  
  // Check if this is a carport (no corner trim for carports)
  const isCarport = buildingStyle.toLowerCase().includes('carport');
  const isShopPort = buildingStyle.toLowerCase().includes('shop_port');
  
  // Enclosure settings
  const leftEnd = config?.leftEnd || 'open';
  const rightEnd = config?.rightEnd || 'open';
  const frontSide = config?.frontSide || 'open';
  const backSide = config?.backSide || 'open';
  const enclosedLength = config?.enclosedLength || 0; // For partial enclosures like shop port
  const enclosedSide = config?.enclosedSide || 'left'; // Which side has the enclosed portion
  
  // Lean-to options
  const leftLean = config?.leftLean || 'none';
  const rightLean = config?.rightLean || 'none';
  
  // Lean-to dimensions
  const leftLeanWidth = config?.leftLeanWidth || 10;
  const leftLeanLength = config?.leftLeanLength || 21;
  const leftLeanHeight = config?.leftLeanHeight || 8;
  const rightLeanWidth = config?.rightLeanWidth || 10;
  const rightLeanLength = config?.rightLeanLength || 21;
  const rightLeanHeight = config?.rightLeanHeight || 8;
  
  // Siding orientation
  const sidingOrientation = config?.sidingOrientation || 'horizontal';
  
  // Roof rib direction: 'vertical' = down slope (default), 'horizontal' = along ridge
  const roofRibDirection = config?.roofRibDirection || 'vertical';
  
  // Doors and windows - direct access from config (rebuild happens via dependency array)
  const garageDoors = config?.garageDoors || [];
  const walkDoors = config?.walkDoors || [];
  const windows = config?.windows || [];
  
  // Serialize door/window positions for dependency comparison (detects position changes)
  const doorsWindowsKey = React.useMemo(() => {
    return JSON.stringify({
      gd: garageDoors.map(d => ({ id: d.id, pos: d.position, w: d.wall })),
      wd: walkDoors.map(d => ({ id: d.id, pos: d.position, w: d.wall })),
      wi: windows.map(w => ({ id: w.id, pos: w.position, w: w.wall }))
    });
  }, [garageDoors, walkDoors, windows]);
  
  // Check if single slope
  const isSingleSlope = roofStyle === 'single_slope';

  // Build the metal building
  const buildBuilding = useCallback((w, l, h, p, rc, tc, wc, wsColor, le, re, fs, bs, singleSlope, sidingDir, gDoors, wDoors, wins, encLen, encSide, lLean, rLean, bStyle, lLeanW, lLeanL, lLeanH, rLeanW, rLeanL, rLeanH, ribDir) => {
    const three = threeRef.current;
    if (!three.scene) return;
    
    const scene = three.scene;
    
    // Clear draggables array
    three.draggables = [];
    
    // Remove old building meshes (keep permanent items like ground)
    const toRemove = [];
    scene.traverse((obj) => {
      if (obj.name === 'building' || obj.name === 'draggable') {
        toRemove.push(obj);
      }
    });
    toRemove.forEach(obj => {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (obj.material.map) obj.material.map.dispose();
        obj.material.dispose();
      }
      scene.remove(obj);
    });

    const textureLoader = new THREE.TextureLoader();
    
    // Load metal panel texture for walls
    const metalTexture = textureLoader.load('/texture.jpg');
    metalTexture.wrapS = THREE.RepeatWrapping;
    metalTexture.wrapT = THREE.RepeatWrapping;
    
    // Load standing seam roof texture (separate texture for roofs)
    const roofPanelTexture = textureLoader.load('/roof-texture.jpg');
    roofPanelTexture.wrapS = THREE.RepeatWrapping;
    roofPanelTexture.wrapT = THREE.RepeatWrapping;

    // Wall material with siding orientation - matching roof ribbing appearance
    const wallMaterial = new THREE.MeshStandardMaterial({
      color: wc,
      map: metalTexture.clone(),
      metalness: 0.4,
      roughness: 0.5,
      side: THREE.DoubleSide
    });
    
    // Wainscot material (36" = 3 feet decorative panel on lower walls)
    const wainscotHeight = 3; // 36 inches = 3 feet
    const hasWainscot = wsColor && wsColor !== 'none';
    const wainscotMaterial = hasWainscot ? new THREE.MeshStandardMaterial({
      color: wsColor,
      map: metalTexture.clone(),
      metalness: 0.4,
      roughness: 0.5,
      side: THREE.DoubleSide
    }) : null;
    
    if (wainscotMaterial) {
      wainscotMaterial.map.repeat.set(4, 1);
      wainscotMaterial.map.wrapS = THREE.RepeatWrapping;
      wainscotMaterial.map.wrapT = THREE.RepeatWrapping;
    }
    
    // Set texture repeat and rotation based on siding direction
    // Using similar repeat ratio as roof for consistent ribbing appearance
    if (sidingDir === 'vertical') {
      wallMaterial.map.repeat.set(1, h / 4);
      wallMaterial.map.rotation = Math.PI / 2;
      wallMaterial.map.center.set(0.5, 0.5);
    } else {
      wallMaterial.map.repeat.set(l / 4, 1);
    }
    wallMaterial.map.wrapS = THREE.RepeatWrapping;
    wallMaterial.map.wrapT = THREE.RepeatWrapping;

    const roofTexture = roofPanelTexture.clone();
    roofTexture.repeat.set(l / 4, 1);
    const roofMaterial = new THREE.MeshStandardMaterial({
      color: rc,
      map: roofTexture,
      metalness: 0.5,
      roughness: 0.4,
      side: THREE.DoubleSide
    });

    const trimMaterial = new THREE.MeshStandardMaterial({
      color: tc,
      metalness: 0.5,
      roughness: 0.4,
      side: THREE.DoubleSide
    });
    
    // Door material - white
    const doorMaterial = new THREE.MeshStandardMaterial({
      color: '#FFFFFF',
      metalness: 0.4,
      roughness: 0.6,
      side: THREE.DoubleSide
    });
    
    // Window material - glass-like
    const glassMaterial = new THREE.MeshStandardMaterial({
      color: '#87CEEB',
      metalness: 0.9,
      roughness: 0.1,
      transparent: true,
      opacity: 0.6,
      side: THREE.DoubleSide
    });
    
    // Window frame material
    const windowFrameMaterial = new THREE.MeshStandardMaterial({
      color: '#FFFFFF',
      metalness: 0.3,
      roughness: 0.5,
    });

    // Concrete slab
    const concreteTexture = textureLoader.load('/concreat.jpg');
    concreteTexture.wrapS = THREE.RepeatWrapping;
    concreteTexture.wrapT = THREE.RepeatWrapping;
    concreteTexture.repeat.set(2, 2);
    
    const slabGeo = new THREE.BoxGeometry(l + 2, 0.3, w + 2);
    const slabMat = new THREE.MeshStandardMaterial({
      map: concreteTexture,
      color: 0x999999,
      roughness: 0.9
    });
    const slab = new THREE.Mesh(slabGeo, slabMat);
    slab.position.set(0, -0.15, 0);
    slab.receiveShadow = true;
    slab.name = 'building';
    scene.add(slab);

    // Overhang amount
    const overhang = 1;
    const sideHang = 0.5;

    // === WALLS ===
    
    // Helper function to create wall material with correct orientation
    const createWallMat = (wallLength) => {
      const mat = wallMaterial.clone();
      mat.map = metalTexture.clone();
      if (sidingDir === 'horizontal') {
        // Rotate texture 90 degrees to make ribs horizontal
        mat.map.repeat.set(wallLength / 3, 1);
        mat.map.rotation = Math.PI / 2;
        mat.map.center.set(0.5, 0.5);
      } else {
        // Vertical ribs - no rotation needed
        mat.map.repeat.set(wallLength / 4, 1);
      }
      mat.map.wrapS = THREE.RepeatWrapping;
      mat.map.wrapT = THREE.RepeatWrapping;
      return mat;
    };
    
    // Helper to get openings for a specific wall
    const getWallOpenings = (wallId) => {
      const openings = [];
      
      // Get wall dimensions based on wall ID
      let wallWidth;
      if (wallId === 'leftEnd' || wallId === 'rightEnd') {
        wallWidth = w;
      } else if (wallId === 'divider') {
        wallWidth = w; // Divider wall spans the width
      } else {
        wallWidth = l;
      }
      
      // Process garage doors
      (gDoors || []).filter(d => d.wall === wallId).forEach(door => {
        const doorX = (door.position / 100) * wallWidth - wallWidth / 2;
        openings.push({
          id: door.id,
          itemType: 'garageDoors',
          type: 'garage',
          x: doorX,
          y: door.height / 2,
          width: door.width,
          height: door.height,
          wall: wallId,
          wallWidth: wallWidth
        });
      });
      
      // Process walk doors
      (wDoors || []).filter(d => d.wall === wallId).forEach(door => {
        const doorX = (door.position / 100) * wallWidth - wallWidth / 2;
        openings.push({
          id: door.id,
          itemType: 'walkDoors',
          type: 'walk',
          x: doorX,
          y: door.height / 2,
          width: door.width,
          height: door.height,
          wall: wallId,
          wallWidth: wallWidth
        });
      });
      
      // Process windows
      (wins || []).filter(win => win.wall === wallId).forEach(win => {
        const winX = (win.position / 100) * wallWidth - wallWidth / 2;
        const winY = h * 0.5; // Windows at 50% height
        openings.push({
          id: win.id,
          itemType: 'windows',
          type: 'window',
          x: winX,
          y: winY,
          width: win.width,
          height: win.height,
          wall: wallId,
          wallWidth: wallWidth
        });
      });
      
      return openings;
    };
    
    // Helper function to create a wall with openings using Shape and ExtrudeGeometry
    const createWallWithOpenings = (wallWidth, wallHeight, openings, isGable = false, gableHeight = 0) => {
      // Create main wall shape
      const shape = new THREE.Shape();
      
      if (isGable) {
        // Gable wall with peaked top
        shape.moveTo(-wallWidth / 2, 0);
        shape.lineTo(wallWidth / 2, 0);
        shape.lineTo(wallWidth / 2, wallHeight);
        shape.lineTo(0, wallHeight + gableHeight);
        shape.lineTo(-wallWidth / 2, wallHeight);
        shape.closePath();
      } else {
        // Rectangular wall
        shape.moveTo(-wallWidth / 2, 0);
        shape.lineTo(wallWidth / 2, 0);
        shape.lineTo(wallWidth / 2, wallHeight);
        shape.lineTo(-wallWidth / 2, wallHeight);
        shape.closePath();
      }
      
      // Add holes for openings (counter-clockwise winding for holes in THREE.js)
      openings.forEach(opening => {
        const hole = new THREE.Path();
        const halfW = opening.width / 2;
        const halfH = opening.height / 2;
        
        // Counter-clockwise winding for holes
        hole.moveTo(opening.x - halfW, opening.y - halfH);
        hole.lineTo(opening.x - halfW, opening.y + halfH);
        hole.lineTo(opening.x + halfW, opening.y + halfH);
        hole.lineTo(opening.x + halfW, opening.y - halfH);
        hole.closePath();
        
        shape.holes.push(hole);
      });
      
      // Use ExtrudeGeometry with very small depth for better hole rendering
      const extrudeSettings = {
        depth: 0.1,
        bevelEnabled: false
      };
      const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      
      // Center the geometry on the Z axis (since extrude goes in +Z direction)
      geometry.translate(0, 0, -0.05);
      
      return geometry;
    };
    
    // Helper to add door/window visuals
    const addOpeningVisuals = (wallId, openings) => {
      openings.forEach(opening => {
        // Create a group to hold all parts of this door/window
        const group = new THREE.Group();
        group.name = 'draggable';
        group.userData = {
          id: opening.id,
          itemType: opening.itemType,
          wall: opening.wall,
          wallWidth: opening.wallWidth,
          type: opening.type,
          // Store dimensions directly in userData to avoid closure issues
          itemWidth: opening.width,
          itemHeight: opening.height
        };
        
        if (opening.type === 'garage' || opening.type === 'walk') {
          // Create door panel - this is the main draggable surface
          // Make it FILL the hole completely (no gap around edges)
          const doorGeo = new THREE.PlaneGeometry(opening.width, opening.height);
          const doorMesh = new THREE.Mesh(doorGeo, doorMaterial.clone());
          doorMesh.name = 'draggable-surface';
          
          // Position based on wall (relative to group)
          doorMesh.position.set(0, 0, 0);
          group.add(doorMesh);
          
          // Add door frame trim - 2 inches
          const frameThickness = 0.167; // 2 inches
          const frameDepth = 0.15;
          
          // Top frame
          const topFrameGeo = new THREE.BoxGeometry(opening.width + frameThickness * 2, frameThickness, frameDepth);
          const topFrame = new THREE.Mesh(topFrameGeo, trimMaterial.clone());
          topFrame.position.set(0, opening.height / 2 + frameThickness / 2, 0.05);
          group.add(topFrame);
          
          // Bottom frame
          const bottomFrameGeo = new THREE.BoxGeometry(opening.width + frameThickness * 2, frameThickness, frameDepth);
          const bottomFrame = new THREE.Mesh(bottomFrameGeo, trimMaterial.clone());
          bottomFrame.position.set(0, -opening.height / 2 - frameThickness / 2, 0.05);
          group.add(bottomFrame);
          
          // Left frame
          const sideFrameGeo = new THREE.BoxGeometry(frameThickness, opening.height + frameThickness * 2, frameDepth);
          const leftFrame = new THREE.Mesh(sideFrameGeo, trimMaterial.clone());
          leftFrame.position.set(-opening.width / 2 - frameThickness / 2, 0, 0.05);
          group.add(leftFrame);
          
          // Right frame
          const rightFrame = new THREE.Mesh(sideFrameGeo, trimMaterial.clone());
          rightFrame.position.set(opening.width / 2 + frameThickness / 2, 0, 0.05);
          group.add(rightFrame);
          
          // Add door panel/face with proper depth - this fills the hole
          // The panel is positioned slightly in front of where the wall hole is
          const doorPanelGeo = new THREE.BoxGeometry(opening.width + 0.2, opening.height + 0.2, 0.2);
          const doorPanelMat = new THREE.MeshStandardMaterial({
            color: 0xf0f0f0, // Light gray/white door base
            metalness: 0.3,
            roughness: 0.7,
            side: THREE.DoubleSide
          });
          const doorPanel = new THREE.Mesh(doorPanelGeo, doorPanelMat);
          doorPanel.position.set(0, 0, 0.02); // Positioned at the wall surface
          group.add(doorPanel);
          
          // Add horizontal ribbed texture for garage doors (like metal panel siding)
          if (opening.type === 'garage') {
            // Dense horizontal ribs - approximately 3 inches apart
            const ribSpacing = 0.25; // 3 inches = 0.25 feet
            const ribCount = Math.floor(opening.height / ribSpacing);
            const ribHeight = 0.04; // Thin raised ribs
            const ribDepth = 0.03; // Subtle depth
            
            // Create ribbed texture across entire door
            for (let i = 0; i < ribCount; i++) {
              const ribY = (i * ribSpacing) - opening.height / 2 + ribSpacing / 2;
              
              // Each rib is a thin horizontal raised line
              const ribGeo = new THREE.BoxGeometry(opening.width - 0.1, ribHeight, ribDepth);
              const ribMat = new THREE.MeshStandardMaterial({
                color: 0xe0e0e0, // Light gray
                metalness: 0.5,
                roughness: 0.4
              });
              const rib = new THREE.Mesh(ribGeo, ribMat);
              rib.position.set(0, ribY, 0.12);
              group.add(rib);
            }
            
            // Add subtle frame around the door
            const frameThick = 0.08;
            const frameDepth = 0.05;
            const frameMat = new THREE.MeshStandardMaterial({
              color: 0xc0c0c0,
              metalness: 0.6,
              roughness: 0.3
            });
            
            // Top frame
            const topFrameGeo = new THREE.BoxGeometry(opening.width, frameThick, frameDepth);
            const topFrame = new THREE.Mesh(topFrameGeo, frameMat);
            topFrame.position.set(0, opening.height/2 - frameThick/2, 0.14);
            group.add(topFrame);
            
            // Bottom frame
            const bottomFrame = new THREE.Mesh(topFrameGeo.clone(), frameMat.clone());
            bottomFrame.position.set(0, -opening.height/2 + frameThick/2, 0.14);
            group.add(bottomFrame);
            
            // Left frame
            const sideFrameGeo = new THREE.BoxGeometry(frameThick, opening.height, frameDepth);
            const leftFrame = new THREE.Mesh(sideFrameGeo, frameMat.clone());
            leftFrame.position.set(-opening.width/2 + frameThick/2, 0, 0.14);
            group.add(leftFrame);
            
            // Right frame
            const rightFrame = new THREE.Mesh(sideFrameGeo.clone(), frameMat.clone());
            rightFrame.position.set(opening.width/2 - frameThick/2, 0, 0.14);
            group.add(rightFrame);
          }
          
          // Add walk-in door specific details
          if (opening.type === 'walk') {
            // Door panels (typical 6-panel door style)
            const panelRows = 3;
            const panelCols = 2;
            const panelWidth = (opening.width - 0.5) / panelCols;
            const panelHeight = (opening.height - 0.4) / panelRows;
            
            for (let row = 0; row < panelRows; row++) {
              for (let col = 0; col < panelCols; col++) {
                const panelX = (col - 0.5) * panelWidth;
                const panelY = (row - 1) * panelHeight;
                
                // Raised panel
                const raisedGeo = new THREE.BoxGeometry(panelWidth - 0.15, panelHeight - 0.15, 0.04);
                const raisedMat = new THREE.MeshStandardMaterial({
                  color: 0xe5e5e5,
                  metalness: 0.2,
                  roughness: 0.8
                });
                const raised = new THREE.Mesh(raisedGeo, raisedMat);
                raised.position.set(panelX, panelY, 0.08);
                group.add(raised);
              }
            }
            
            // Door handle
            const handleGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.15, 8);
            const handleMat = new THREE.MeshStandardMaterial({
              color: 0x333333,
              metalness: 0.8,
              roughness: 0.3
            });
            const handle = new THREE.Mesh(handleGeo, handleMat);
            handle.rotation.x = Math.PI / 2;
            handle.position.set(opening.width / 2 - 0.3, 0, 0.12);
            group.add(handle);
          }
          
          // Position the group based on wall
          // Doors should be on the OUTSIDE of the wall (visible from exterior)
          // Wall depth is 0.1 (from ExtrudeGeometry), so outer surface is at wall_position - 0.05
          if (wallId === 'leftEnd') {
            // Left end wall center at x = -l/2, outer surface at -l/2 - 0.05
            // Position group so door face aligns with outer wall surface
            group.position.set(-l / 2 - 0.05, opening.y, opening.x);
            group.rotation.y = -Math.PI / 2; // Rotate so door's +Z faces global -X (outward)
          } else if (wallId === 'rightEnd') {
            // Right end wall center at x = l/2, outer surface at l/2 + 0.05
            group.position.set(l / 2 + 0.05, opening.y, opening.x);
            group.rotation.y = Math.PI / 2; // Rotate so door's +Z faces global +X (outward)
          } else if (wallId === 'frontSide') {
            // Front side wall center at z = -w/2, outer surface at -w/2 - 0.05
            group.position.set(opening.x, opening.y, -w / 2 - 0.05);
            group.rotation.y = Math.PI; // Rotate so door's +Z faces global -Z (outward)
          } else if (wallId === 'backSide') {
            // Back side wall center at z = w/2, outer surface at w/2 + 0.05
            group.position.set(opening.x, opening.y, w / 2 + 0.05);
            group.rotation.y = 0; // Door's +Z already faces global +Z (outward)
          } else if (wallId === 'divider') {
            // Divider wall position depends on encSide
            if (encSide === 'right') {
              // Divider is at x = l/2 - encLen, facing toward the carport (-X)
              group.position.set(l / 2 - encLen - 0.05, opening.y, opening.x);
              group.rotation.y = Math.PI / 2;
            } else {
              // Divider is at x = -l/2 + encLen, facing toward the carport (+X)
              group.position.set(-l / 2 + encLen + 0.05, opening.y, opening.x);
              group.rotation.y = -Math.PI / 2;
            }
          }
          
        } else if (opening.type === 'window') {
          // Create window glass - this is the main draggable surface
          const glassGeo = new THREE.PlaneGeometry(opening.width - 0.2, opening.height - 0.2);
          const glass = new THREE.Mesh(glassGeo, glassMaterial.clone());
          glass.name = 'draggable-surface';
          glass.position.set(0, 0, 0);
          group.add(glass);
          
          // Window frame - 2 inches trim
          const frameThickness = 0.167; // 2 inches
          const frameDepth = 0.15;
          
          // Create frame pieces using trim material
          const topFrameGeo = new THREE.BoxGeometry(opening.width + frameThickness * 2, frameThickness, frameDepth);
          const sideFrameGeo = new THREE.BoxGeometry(frameThickness, opening.height + frameThickness * 2, frameDepth);
          
          const topFrame = new THREE.Mesh(topFrameGeo, trimMaterial.clone());
          const bottomFrame = new THREE.Mesh(topFrameGeo, trimMaterial.clone());
          const leftFrame = new THREE.Mesh(sideFrameGeo, trimMaterial.clone());
          const rightFrame = new THREE.Mesh(sideFrameGeo, trimMaterial.clone());
          
          topFrame.position.set(0, opening.height / 2 + frameThickness / 2, 0);
          bottomFrame.position.set(0, -opening.height / 2 - frameThickness / 2, 0);
          leftFrame.position.set(-opening.width / 2 - frameThickness / 2, 0, 0);
          rightFrame.position.set(opening.width / 2 + frameThickness / 2, 0, 0);
          
          group.add(topFrame);
          group.add(bottomFrame);
          group.add(leftFrame);
          group.add(rightFrame);
          
          // Add center cross for window
          const crossHGeo = new THREE.BoxGeometry(opening.width - 0.1, 0.05, 0.08);
          const crossVGeo = new THREE.BoxGeometry(0.05, opening.height - 0.1, 0.08);
          const crossH = new THREE.Mesh(crossHGeo, trimMaterial.clone());
          const crossV = new THREE.Mesh(crossVGeo, trimMaterial.clone());
          crossH.position.set(0, 0, 0.05);
          crossV.position.set(0, 0, 0.05);
          group.add(crossH);
          group.add(crossV);
          
          // Position the group based on wall
          if (wallId === 'leftEnd') {
            group.position.set(-l / 2 - 0.05, opening.y, opening.x);
            group.rotation.y = Math.PI / 2;
          } else if (wallId === 'rightEnd') {
            group.position.set(l / 2 + 0.05, opening.y, opening.x);
            group.rotation.y = -Math.PI / 2;
          } else if (wallId === 'frontSide') {
            group.position.set(opening.x, opening.y, -w / 2 - 0.05);
          } else if (wallId === 'backSide') {
            group.position.set(opening.x, opening.y, w / 2 + 0.05);
            group.rotation.y = Math.PI;
          } else if (wallId === 'divider') {
            // Divider wall position depends on encSide
            if (encSide === 'right') {
              // Divider is at x = l/2 - encLen, facing toward the carport (-X)
              group.position.set(l / 2 - encLen - 0.05, opening.y, opening.x);
              group.rotation.y = Math.PI / 2;
            } else {
              // Divider is at x = -l/2 + encLen, facing toward the carport (+X)
              group.position.set(-l / 2 + encLen + 0.05, opening.y, opening.x);
              group.rotation.y = -Math.PI / 2;
            }
          }
        }
        
        group.castShadow = true;
        scene.add(group);
        
        // Add to draggables array for raycasting
        three.draggables.push(group);
      });
    };
    
    // Helper function to add wainscot panel to a wall
    const addWainscot = (wallWidth, wallPosition, rotation, isGable = false) => {
      if (!hasWainscot) return;
      
      // Wainscot is 36 inches (3 feet) tall panel at the bottom of the wall
      const wsHeight = Math.min(wainscotHeight, h * 0.4); // Max 40% of wall height
      const wsGeo = new THREE.BoxGeometry(wallWidth, wsHeight, 0.12);
      const ws = new THREE.Mesh(wsGeo, wainscotMaterial.clone());
      
      // Position at the bottom of the wall, slightly in front
      ws.position.set(wallPosition.x, wsHeight / 2, wallPosition.z);
      ws.rotation.y = rotation;
      
      // Offset slightly outward from the wall
      const offset = 0.08;
      if (rotation === 0) {
        ws.position.z -= offset;
      } else if (rotation === Math.PI) {
        ws.position.z += offset;
      } else if (rotation === Math.PI / 2) {
        ws.position.x += offset;
      } else if (rotation === -Math.PI / 2) {
        ws.position.x -= offset;
      }
      
      ws.castShadow = true;
      ws.receiveShadow = true;
      ws.name = 'building';
      scene.add(ws);
      
      // Add a trim strip at the top of the wainscot
      const trimGeo = new THREE.BoxGeometry(wallWidth, 0.1, 0.15);
      const trimStrip = new THREE.Mesh(trimGeo, trimMaterial.clone());
      trimStrip.position.set(ws.position.x, wsHeight, ws.position.z);
      trimStrip.rotation.y = rotation;
      trimStrip.castShadow = true;
      trimStrip.name = 'building';
      scene.add(trimStrip);
    };
    
    // Right wall (positive Z) - backSide
    if (bs === 'enclosed') {
      const wallOpenings = getWallOpenings('backSide');
      const wallGeo = createWallWithOpenings(l, h, wallOpenings);
      const rightWall = new THREE.Mesh(wallGeo, createWallMat(l));
      rightWall.position.set(0, 0, w / 2);
      rightWall.rotation.y = Math.PI;
      rightWall.castShadow = true;
      rightWall.receiveShadow = true;
      rightWall.name = 'building';
      scene.add(rightWall);
      
      // Add door/window visuals
      addOpeningVisuals('backSide', wallOpenings);
      
      // Add wainscot
      addWainscot(l, { x: 0, z: w / 2 }, Math.PI);
    } else if (bs === 'top-1.5' || bs === 'top-3' || bs === 'top-6') {
      // Top panel - wall at top of side, open below
      const panelHeight = bs === 'top-1.5' ? 1.5 : bs === 'top-3' ? 3 : 6;
      const panelGeo = new THREE.BoxGeometry(l, panelHeight, 0.1);
      const topPanel = new THREE.Mesh(panelGeo, createWallMat(l));
      topPanel.position.set(0, h - panelHeight / 2, w / 2);
      topPanel.rotation.y = Math.PI;
      topPanel.castShadow = true;
      topPanel.receiveShadow = true;
      topPanel.name = 'building';
      scene.add(topPanel);
    } else if (bs === 'partial' && encLen > 0) {
      // Partial wall - cover only the enclosed portion
      const partialWallGeo = createWallWithOpenings(encLen, h, []);
      const partialWall = new THREE.Mesh(partialWallGeo, createWallMat(encLen));
      if (encSide === 'right') {
        // Enclosed on right side
        partialWall.position.set(l/2 - encLen/2, 0, w / 2);
      } else {
        // Enclosed on left side
        partialWall.position.set(-l/2 + encLen/2, 0, w / 2);
      }
      partialWall.rotation.y = Math.PI;
      partialWall.castShadow = true;
      partialWall.receiveShadow = true;
      partialWall.name = 'building';
      scene.add(partialWall);
    }

    // Left wall (negative Z) - frontSide
    if (fs === 'enclosed') {
      const wallOpenings = getWallOpenings('frontSide');
      const wallGeo = createWallWithOpenings(l, h, wallOpenings);
      const leftWall = new THREE.Mesh(wallGeo, createWallMat(l));
      leftWall.position.set(0, 0, -w / 2);
      leftWall.castShadow = true;
      leftWall.receiveShadow = true;
      leftWall.name = 'building';
      scene.add(leftWall);
      
      // Add door/window visuals
      addOpeningVisuals('frontSide', wallOpenings);
      
      // Add wainscot
      addWainscot(l, { x: 0, z: -w / 2 }, 0);
    } else if (fs === 'top-1.5' || fs === 'top-3' || fs === 'top-6') {
      // Top panel - wall at top of side, open below
      const panelHeight = fs === 'top-1.5' ? 1.5 : fs === 'top-3' ? 3 : 6;
      const panelGeo = new THREE.BoxGeometry(l, panelHeight, 0.1);
      const topPanel = new THREE.Mesh(panelGeo, createWallMat(l));
      topPanel.position.set(0, h - panelHeight / 2, -w / 2);
      topPanel.castShadow = true;
      topPanel.receiveShadow = true;
      topPanel.name = 'building';
      scene.add(topPanel);
    } else if (fs === 'partial' && encLen > 0) {
      // Partial wall - cover only the enclosed portion
      const partialWallGeo = createWallWithOpenings(encLen, h, []);
      const partialWall = new THREE.Mesh(partialWallGeo, createWallMat(encLen));
      if (encSide === 'right') {
        // Enclosed on right side
        partialWall.position.set(l/2 - encLen/2, 0, -w / 2);
      } else {
        // Enclosed on left side
        partialWall.position.set(-l/2 + encLen/2, 0, -w / 2);
      }
      partialWall.castShadow = true;
      partialWall.receiveShadow = true;
      partialWall.name = 'building';
      scene.add(partialWall);
    }

    // Front wall (negative X) - leftEnd with gable
    if (le === 'gable') {
      // Gable only - just the triangular peak, no wall below
      const gableHeight = !singleSlope ? p : 0;
      
      if (gableHeight > 0) {
        // Create triangular shape for gable only
        const shape = new THREE.Shape();
        shape.moveTo(-w / 2, h);          // Bottom left at eave
        shape.lineTo(w / 2, h);           // Bottom right at eave
        shape.lineTo(0, h + gableHeight); // Peak of gable
        shape.closePath();
        
        const extrudeSettings = { depth: 0.1, bevelEnabled: false };
        const gableGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        gableGeo.translate(0, 0, -0.05);
        
        const frontWall = new THREE.Mesh(gableGeo, createWallMat(w));
        frontWall.position.set(-l / 2, 0, 0);
        frontWall.rotation.y = -Math.PI / 2;
        frontWall.castShadow = true;
        frontWall.receiveShadow = true;
        frontWall.name = 'building';
        scene.add(frontWall);
      }
    } else if (le === 'extended') {
      // Extended gable end - gable + top half of wall, bottom half open
      const extendedHeight = h / 2 + 0.67; // Half wall height + 8 inches higher
      const gableHeight = !singleSlope ? p : 0; // Use pitch value directly like enclosed walls
      
      // Create custom shape: gable peak + half wall
      const shape = new THREE.Shape();
      shape.moveTo(-w / 2, extendedHeight); // Start at left, halfway up
      shape.lineTo(w / 2, extendedHeight);  // Bottom right of extended section
      shape.lineTo(w / 2, h);               // Top right at eave
      shape.lineTo(0, h + gableHeight);     // Peak of gable
      shape.lineTo(-w / 2, h);              // Top left at eave
      shape.closePath();
      
      const extrudeSettings = { depth: 0.1, bevelEnabled: false };
      const extendedGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      extendedGeo.translate(0, 0, -0.05);
      
      const frontWall = new THREE.Mesh(extendedGeo, createWallMat(w));
      frontWall.position.set(-l / 2, 0, 0);
      frontWall.rotation.y = -Math.PI / 2;
      frontWall.castShadow = true;
      frontWall.receiveShadow = true;
      frontWall.name = 'building';
      scene.add(frontWall);
    } else if (le !== 'open') {
      const wallOpenings = getWallOpenings('leftEnd');
      const wallGeo = createWallWithOpenings(w, h, wallOpenings, !singleSlope, p);
      const frontWall = new THREE.Mesh(wallGeo, createWallMat(w));
      frontWall.position.set(-l / 2, 0, 0);
      frontWall.rotation.y = -Math.PI / 2;
      frontWall.castShadow = true;
      frontWall.receiveShadow = true;
      frontWall.name = 'building';
      scene.add(frontWall);
      
      // Add door/window visuals
      addOpeningVisuals('leftEnd', wallOpenings);
      
      // Add wainscot
      addWainscot(w, { x: -l / 2, z: 0 }, -Math.PI / 2);
    }

    // Back wall (positive X) - rightEnd with gable
    if (re === 'gable') {
      // Gable only - just the triangular peak, no wall below
      const gableHeight = !singleSlope ? p : 0;
      
      if (gableHeight > 0) {
        // Create triangular shape for gable only
        const shape = new THREE.Shape();
        shape.moveTo(-w / 2, h);          // Bottom left at eave
        shape.lineTo(w / 2, h);           // Bottom right at eave
        shape.lineTo(0, h + gableHeight); // Peak of gable
        shape.closePath();
        
        const extrudeSettings = { depth: 0.1, bevelEnabled: false };
        const gableGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        gableGeo.translate(0, 0, -0.05);
        
        const backWall = new THREE.Mesh(gableGeo, createWallMat(w));
        backWall.position.set(l / 2, 0, 0);
        backWall.rotation.y = Math.PI / 2;
        backWall.castShadow = true;
        backWall.receiveShadow = true;
        backWall.name = 'building';
        scene.add(backWall);
      }
    } else if (re === 'extended') {
      // Extended gable end - gable + top half of wall, bottom half open
      const extendedHeight = h / 2 + 0.67; // Half wall height + 8 inches higher
      const gableHeight = !singleSlope ? p : 0; // Use pitch value directly like enclosed walls
      
      // Create custom shape: gable peak + half wall
      const shape = new THREE.Shape();
      shape.moveTo(-w / 2, extendedHeight); // Start at left, halfway up
      shape.lineTo(w / 2, extendedHeight);  // Bottom right of extended section
      shape.lineTo(w / 2, h);               // Top right at eave
      shape.lineTo(0, h + gableHeight);     // Peak of gable
      shape.lineTo(-w / 2, h);              // Top left at eave
      shape.closePath();
      
      const extrudeSettings = { depth: 0.1, bevelEnabled: false };
      const extendedGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
      extendedGeo.translate(0, 0, -0.05);
      
      const backWall = new THREE.Mesh(extendedGeo, createWallMat(w));
      backWall.position.set(l / 2, 0, 0);
      backWall.rotation.y = Math.PI / 2;
      backWall.castShadow = true;
      backWall.receiveShadow = true;
      backWall.name = 'building';
      scene.add(backWall);
    } else if (re !== 'open') {
      const wallOpenings = getWallOpenings('rightEnd');
      const wallGeo = createWallWithOpenings(w, h, wallOpenings, !singleSlope, p);
      const backWall = new THREE.Mesh(wallGeo, createWallMat(w));
      backWall.position.set(l / 2, 0, 0);
      backWall.rotation.y = Math.PI / 2;
      backWall.castShadow = true;
      backWall.receiveShadow = true;
      backWall.name = 'building';
      scene.add(backWall);
      
      // Add door/window visuals
      addOpeningVisuals('rightEnd', wallOpenings);
      
      // Add wainscot
      addWainscot(w, { x: l / 2, z: 0 }, Math.PI / 2);
    }
    
    // Interior dividing wall for Shop Port (between enclosed shop and open carport)
    if (encLen > 0 && (fs === 'partial' || bs === 'partial')) {
      const dividerOpenings = getWallOpenings('divider');
      const dividerGeo = createWallWithOpenings(w, h, dividerOpenings, false, 0);
      const dividerWall = new THREE.Mesh(dividerGeo, createWallMat(w));
      
      if (encSide === 'right') {
        // Divider wall is between carport (left) and shop (right)
        dividerWall.position.set(l/2 - encLen, 0, 0);
        dividerWall.rotation.y = -Math.PI / 2; // Face toward carport (left)
      } else {
        // Divider wall is between shop (left) and carport (right)
        dividerWall.position.set(-l/2 + encLen, 0, 0);
        dividerWall.rotation.y = Math.PI / 2; // Face toward carport (right)
      }
      dividerWall.castShadow = true;
      dividerWall.receiveShadow = true;
      dividerWall.name = 'building';
      scene.add(dividerWall);
      
      // Add door/window visuals on divider wall
      addOpeningVisuals('divider', dividerOpenings);
    }

    // === ROOFS ===
    // CRITICAL: Roof pitch MUST match the internal structure pitch EXACTLY
    // The structure uses: rafterAngle = Math.atan2(p, innerW / 2) where innerW = w - wallInset * 2
    // The roof must use the SAME angle but extend beyond to hide the structure from outside
    
    const wallInsetForRoof = 0.3; // Same as wallInset used for structure
    const innerWForRoof = w - wallInsetForRoof * 2; // Same inner width as structure
    const structureRafterAngle = Math.atan2(p, innerWForRoof / 2); // EXACT same angle as trusses
    
    if (singleSlope) {
      // Single slope roof using thin BoxGeometry
      const roofLength = l + overhang * 2;
      const roofWidth = w + sideHang * 2;
      const slopeRise = p * 2 + 0.33; // Extra drop at back
      const slopeAngle = Math.atan2(slopeRise, roofWidth);
      const roofPanelWidth = Math.sqrt(roofWidth ** 2 + slopeRise ** 2);
      
      // Thin box for roof
      const roofGeo = new THREE.BoxGeometry(roofLength, 0.05, roofPanelWidth);
      const solidRoofMat = new THREE.MeshStandardMaterial({
        color: roofColor,
        roughness: 0.7,
        metalness: 0.3,
        side: THREE.DoubleSide
      });
      
      const singleRoof = new THREE.Mesh(roofGeo, solidRoofMat);
      
      // Lower the center Y so back edge sits on framework
      const centerY = h + p + 0.15;
      singleRoof.position.set(0, centerY, 0);
      singleRoof.rotation.x = slopeAngle;
      singleRoof.castShadow = true;
      singleRoof.receiveShadow = true;
      singleRoof.name = 'building';
      scene.add(singleRoof);
    } else {
      // Standard A-frame roof - 1/8 inch thick, NO ridge cap
      // Roof sits directly ON TOP of the internal truss rafters
      
      const roofThickness = 0.01; // 1/8 inch = 0.125/12 ft ≈ 0.01 ft
      const roofLength = l + overhang * 2;
      const eaveOverhang = 1.0;
      
      // Structure inner dimensions (must match truss calculations)
      const structInnerW = w - 0.3 * 2; // wallInset = 0.3
      
      // Roof angle MUST match structure rafters exactly
      const roofAngle = Math.atan2(p, structInnerW / 2);
      
      // Rafter height is 0.25, roof sits on top of rafters
      const rafterHeight = 0.25;
      const rafterTopOffset = rafterHeight / 2;
      
      // Roof panel dimensions
      const roofRun = w/2 + eaveOverhang;
      const roofRise = roofRun * Math.tan(roofAngle);
      const roofPanelLength = Math.sqrt(roofRun ** 2 + roofRise ** 2);
      
      // LEFT ROOF PANEL (-Z side, front)
      const leftRoofGeo = new THREE.BoxGeometry(roofLength, roofThickness, roofPanelLength);
      const leftRoof = new THREE.Mesh(leftRoofGeo, roofMaterial.clone());
      // Ribs run DOWN the slope (from ridge to eave) by default
      // ribDir: 'vertical' = down slope, 'horizontal' = along ridge
      const leftRoofTex = roofPanelTexture.clone();
      if (ribDir === 'horizontal') {
        // Ribs run along the ridge (horizontal)
        leftRoofTex.rotation = Math.PI / 2;
        leftRoofTex.center.set(0.5, 0.5);
        leftRoofTex.repeat.set(roofPanelLength / 2, l / 2);
      } else {
        // Ribs run down the slope (vertical) - DEFAULT
        leftRoofTex.repeat.set(l / 2, roofPanelLength / 2);
      }
      leftRoofTex.wrapS = THREE.RepeatWrapping;
      leftRoofTex.wrapT = THREE.RepeatWrapping;
      leftRoof.material.map = leftRoofTex;
      
      // Position so roof TOUCHES the rafters
      const eaveZ = -(w/2 + eaveOverhang);
      const ridgeZ = 0;
      const eaveY = h - eaveOverhang * Math.tan(roofAngle) + rafterTopOffset;
      const ridgeY = h + p + rafterTopOffset;
      
      const centerZ = (eaveZ + ridgeZ) / 2;
      const centerY = (eaveY + ridgeY) / 2 + (roofThickness / 2) * Math.cos(roofAngle);
      
      leftRoof.position.set(0, centerY, centerZ);
      leftRoof.rotation.x = -roofAngle;
      leftRoof.castShadow = true;
      leftRoof.receiveShadow = true;
      leftRoof.name = 'building';
      scene.add(leftRoof);

      // RIGHT ROOF PANEL (+Z side, back) - mirror of left
      const rightRoofGeo = new THREE.BoxGeometry(roofLength, roofThickness, roofPanelLength);
      const rightRoof = new THREE.Mesh(rightRoofGeo, roofMaterial.clone());
      const rightRoofTex = roofPanelTexture.clone();
      if (ribDir === 'horizontal') {
        rightRoofTex.rotation = Math.PI / 2;
        rightRoofTex.center.set(0.5, 0.5);
        rightRoofTex.repeat.set(roofPanelLength / 2, l / 2);
      } else {
        rightRoofTex.repeat.set(l / 2, roofPanelLength / 2);
      }
      rightRoofTex.wrapS = THREE.RepeatWrapping;
      rightRoofTex.wrapT = THREE.RepeatWrapping;
      rightRoof.material.map = rightRoofTex;
      
      rightRoof.position.set(0, centerY, -centerZ);
      rightRoof.rotation.x = roofAngle;
      rightRoof.castShadow = true;
      rightRoof.receiveShadow = true;
      rightRoof.name = 'building';
      scene.add(rightRoof);
      
      // NO RIDGE CAP - removed as requested
    }

    // === TRIM (Corner posts) - inside the walls ===
    const postGeo = new THREE.BoxGeometry(0.3, h, 0.3); // Box geometry for consistency
    const wallInset = 0.3; // How far inside the wall the structure sits
    
    // For single slope, front posts (high side at -z) extend to roof height
    const frontPostHeight = singleSlope ? (h + p * 2) : h;
    const frontPostGeo = singleSlope ? new THREE.BoxGeometry(0.3, frontPostHeight, 0.3) : postGeo;
    
    // Four corner posts (inside corners)
    // corners format: [x, z, isFrontSide]
    const corners = [
      [-l/2 + wallInset, w/2 - wallInset, false],   // back-left (low side for single slope)
      [-l/2 + wallInset, -w/2 + wallInset, true],   // front-left (high side for single slope)
      [l/2 - wallInset, w/2 - wallInset, false],    // back-right (low side for single slope)
      [l/2 - wallInset, -w/2 + wallInset, true]     // front-right (high side for single slope)
    ];
    
    // Store corner data for web truss addition later
    const cornerData = [];
    
    corners.forEach(([x, z, isFront]) => {
      const useHeight = (singleSlope && isFront) ? frontPostHeight : h;
      const useGeo = (singleSlope && isFront) ? frontPostGeo : postGeo;
      const post = new THREE.Mesh(useGeo, trimMaterial);
      post.position.set(x, useHeight / 2, z);
      post.castShadow = true;
      post.name = 'building';
      scene.add(post);
      
      // Store corner info for web truss addition later
      cornerData.push({ x, z, isFront, useHeight });
    });

    // === CORNER KNEE BRACES ===
    // Add knee braces to all four corner legs
    const cornerKneeStartY = h * 0.75;
    const cornerKneeHorizontalRun = 2;
    const cornerRoofRise = p; // Use pitch for roof rise
    const cornerRafterProgress = cornerKneeHorizontalRun / (w/2);
    const cornerKneeEndY = h + (cornerRafterProgress * cornerRoofRise);
    const cornerKneeRise = cornerKneeEndY - cornerKneeStartY;
    const cornerKneeLength = Math.sqrt(cornerKneeRise ** 2 + cornerKneeHorizontalRun ** 2);
    const cornerKneeAngle = Math.atan2(cornerKneeRise, cornerKneeHorizontalRun);
    const cornerKneeGeo = new THREE.BoxGeometry(0.2, 0.2, cornerKneeLength);
    
    // Corner brace material (same as trusses - white)
    const cornerBraceMat = new THREE.MeshStandardMaterial({
      color: '#B5B5B3', // Light grey for all structural elements
      metalness: 0.3,
      roughness: 0.5
    });
    
    if (!singleSlope) {
      // Front-left corner (x = -l/2, z = -w/2) - brace goes toward center
      const flBrace = new THREE.Mesh(cornerKneeGeo, cornerBraceMat);
      flBrace.position.set(
        -l/2 + wallInset,
        (cornerKneeStartY + cornerKneeEndY) / 2,
        -w/2 + wallInset + cornerKneeHorizontalRun/2
      );
      flBrace.rotation.x = -cornerKneeAngle;
      flBrace.name = 'building';
      scene.add(flBrace);
      
      // Front-right corner (x = -l/2, z = w/2) - brace goes toward center
      const frBrace = new THREE.Mesh(cornerKneeGeo, cornerBraceMat);
      frBrace.position.set(
        -l/2 + wallInset,
        (cornerKneeStartY + cornerKneeEndY) / 2,
        w/2 - wallInset - cornerKneeHorizontalRun/2
      );
      frBrace.rotation.x = cornerKneeAngle;
      frBrace.name = 'building';
      scene.add(frBrace);
      
      // Back-left corner (x = l/2, z = -w/2) - brace goes toward center
      const blBrace = new THREE.Mesh(cornerKneeGeo, cornerBraceMat);
      blBrace.position.set(
        l/2 - wallInset,
        (cornerKneeStartY + cornerKneeEndY) / 2,
        -w/2 + wallInset + cornerKneeHorizontalRun/2
      );
      blBrace.rotation.x = -cornerKneeAngle;
      blBrace.name = 'building';
      scene.add(blBrace);
      
      // Back-right corner (x = l/2, z = w/2) - brace goes toward center
      const brBrace = new THREE.Mesh(cornerKneeGeo, cornerBraceMat);
      brBrace.position.set(
        l/2 - wallInset,
        (cornerKneeStartY + cornerKneeEndY) / 2,
        w/2 - wallInset - cornerKneeHorizontalRun/2
      );
      brBrace.rotation.x = cornerKneeAngle;
      brBrace.name = 'building';
      scene.add(brBrace);
    } else {
      // SINGLE SLOPE corner knee braces - matching reference image
      // Braces go from post UP toward the rafter on both sides
      
      const innerWCorner = w - wallInset * 2;
      const ssKneeLength = 3.5; // Knee brace length
      const ssKneeGeo = new THREE.BoxGeometry(0.15, 0.15, ssKneeLength);
      
      // Front posts are at Z = -innerWCorner/2, height = h + p*2
      // Back posts are at Z = innerWCorner/2, height = h
      const frontPostZ = -innerWCorner/2;
      const backPostZ = innerWCorner/2;
      const frontPostTop = h + p * 2;
      const backPostTop = h;
      
      // Front knee braces - from post going UP toward rafter (toward center/+Z)
      const frontBraceStartY = frontPostTop * 0.7 + 0.5; // +6 inches total
      const frontBraceAngle = -Math.PI / 4; // 45 degrees angling up toward rafter
      
      // Front-left corner
      const flKnee = new THREE.Mesh(ssKneeGeo, cornerBraceMat);
      flKnee.position.set(
        -l/2 + wallInset,
        frontBraceStartY + ssKneeLength/2 * Math.sin(Math.PI/4),
        frontPostZ + ssKneeLength/2 * Math.cos(Math.PI/4)
      );
      flKnee.rotation.x = frontBraceAngle;
      flKnee.name = 'building';
      scene.add(flKnee);
      
      // Front-right corner
      const frKnee = new THREE.Mesh(ssKneeGeo, cornerBraceMat);
      frKnee.position.set(
        l/2 - wallInset,
        frontBraceStartY + ssKneeLength/2 * Math.sin(Math.PI/4),
        frontPostZ + ssKneeLength/2 * Math.cos(Math.PI/4)
      );
      frKnee.rotation.x = frontBraceAngle;
      frKnee.name = 'building';
      scene.add(frKnee);
      
      // Back knee braces - from post going UP toward rafter (toward center/-Z)
      const backBraceStartY = backPostTop * 0.7 + 0.25; // +3 inches
      const backBraceAngle = Math.PI / 4; // 45 degrees angling up toward rafter
      
      // Back-left corner
      const blKnee = new THREE.Mesh(ssKneeGeo, cornerBraceMat);
      blKnee.position.set(
        -l/2 + wallInset,
        backBraceStartY + ssKneeLength/2 * Math.sin(Math.PI/4),
        backPostZ - ssKneeLength/2 * Math.cos(Math.PI/4)
      );
      blKnee.rotation.x = backBraceAngle;
      blKnee.name = 'building';
      scene.add(blKnee);
      
      // Back-right corner
      const brKnee = new THREE.Mesh(ssKneeGeo, cornerBraceMat);
      brKnee.position.set(
        l/2 - wallInset,
        backBraceStartY + ssKneeLength/2 * Math.sin(Math.PI/4),
        backPostZ - ssKneeLength/2 * Math.cos(Math.PI/4)
      );
      brKnee.rotation.x = backBraceAngle;
      brKnee.name = 'building';
      scene.add(brKnee);
    }

    // === LEGS AND TRUSSES (inside the walls) ===
    // Spacing: 5ft normally, 2.5ft if building is over 12ft tall
    const legSpacing = h > 12 ? 2.5 : 5;
    const numBays = Math.floor(l / legSpacing);
    const actualSpacing = l / numBays;
    
    // Leg (column) geometry - thicker square steel tubing
    const legGeo = new THREE.BoxGeometry(0.3, h, 0.3);
    
    // Truss material (WHITE for all structural elements)
    const trussMaterial = new THREE.MeshStandardMaterial({
      color: '#B5B5B3', // Light grey for all structural elements
      metalness: 0.3,
      roughness: 0.5
    });
    
    // Inner dimensions (inside walls)
    const innerW = w - wallInset * 2;
    
    // Calculate roof angle - steeper pitch like reference image
    const roofRise = p; // Use the pitch value
    const rafterAngle = Math.atan2(roofRise, innerW / 2);
    const rafterLength = Math.sqrt((innerW / 2) ** 2 + roofRise ** 2);
    
    // === BASE BEAMS - Horizontal beams at bottom running front to back (along X axis) ===
    // Only for carport and 30' carport templates (same size and thickness as legs: 0.3 x 0.3)
    if (bStyle === 'carport' || bStyle === 'carport_30') {
      // Length from first corner post to last corner post
      const baseBeamStartX = -l/2 + wallInset;
      const baseBeamEndX = l/2 - wallInset;
      const baseBeamLength = baseBeamEndX - baseBeamStartX;
      const baseBeamGeo = new THREE.BoxGeometry(baseBeamLength, 0.3, 0.3);
      
      // Front side base beam - flush with front legs (at -w/2 + wallInset)
      const frontLegZ = -w/2 + wallInset;
      const frontBaseBeam = new THREE.Mesh(baseBeamGeo, trussMaterial);
      frontBaseBeam.position.set(0, 0.15, frontLegZ);
      frontBaseBeam.castShadow = true;
      frontBaseBeam.name = 'building';
      scene.add(frontBaseBeam);
      
      // Back side base beam - flush with back legs (at w/2 - wallInset)
      const backLegZ = w/2 - wallInset;
      const backBaseBeam = new THREE.Mesh(baseBeamGeo, trussMaterial);
      backBaseBeam.position.set(0, 0.15, backLegZ);
      backBaseBeam.castShadow = true;
      backBaseBeam.name = 'building';
      scene.add(backBaseBeam);
    }
    
    // Add legs and trusses at each bay (inside the walls)
    for (let i = 1; i < numBays; i++) {
      const xPos = -l/2 + i * actualSpacing;
      
      // For single slope, front legs (high side at -z) extend to roof height
      const frontLegHeight = singleSlope ? (h + roofRise * 2) : h;
      const frontLegGeo = singleSlope ? new THREE.BoxGeometry(0.3, frontLegHeight, 0.3) : legGeo;
      
      // Front side leg (inside, offset from wall) - taller for single slope
      const frontLeg = new THREE.Mesh(frontLegGeo, trussMaterial);
      frontLeg.position.set(xPos, frontLegHeight / 2, -innerW/2);
      frontLeg.castShadow = true;
      frontLeg.name = 'building';
      scene.add(frontLeg);
      
      // Back side leg (inside, offset from wall) - standard height
      const backLeg = new THREE.Mesh(legGeo, trussMaterial);
      backLeg.position.set(xPos, h / 2, innerW/2);
      backLeg.castShadow = true;
      backLeg.name = 'building';
      scene.add(backLeg);
      
      // === LEG WEB TRUSS - Double leg with horizontal connectors ===
      // ONLY for 40' to 50' Carport template
      const isWebTrussLegTemplate = bStyle === 'carport_40_50';
      if (isWebTrussLegTemplate) {
        const legWebSpacing = 1.5; // 18 inches between connectors
        const legInnerOffset = 0.8; // Distance between parallel legs (front to back)
        
        // Front leg - add inner parallel leg (offset toward inside/back)
        const frontInnerLegGeo = new THREE.BoxGeometry(0.2, frontLegHeight, 0.2);
        const frontInnerLeg = new THREE.Mesh(frontInnerLegGeo, trussMaterial);
        frontInnerLeg.position.set(xPos, frontLegHeight / 2, -innerW/2 + legInnerOffset);
        frontInnerLeg.name = 'building';
        scene.add(frontInnerLeg);
        
        // Front leg horizontal connectors (ladder rungs - run along Z axis)
        const numFrontConnectors = Math.floor(frontLegHeight / legWebSpacing);
        const legConnectorGeo = new THREE.BoxGeometry(0.15, 0.15, legInnerOffset + 0.2);
        
        // Add connector at bottom (ground level)
        const frontBottomConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
        frontBottomConnector.position.set(xPos, 0.1, -innerW/2 + legInnerOffset/2);
        frontBottomConnector.name = 'building';
        scene.add(frontBottomConnector);
        
        // Add connector at top
        const frontTopConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
        frontTopConnector.position.set(xPos, frontLegHeight - 0.1, -innerW/2 + legInnerOffset/2);
        frontTopConnector.name = 'building';
        scene.add(frontTopConnector);
        
        // Add intermediate connectors
        for (let c = 1; c < numFrontConnectors; c++) {
          const connectorY = c * legWebSpacing;
          const frontConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
          frontConnector.position.set(xPos, connectorY, -innerW/2 + legInnerOffset/2);
          frontConnector.name = 'building';
          scene.add(frontConnector);
        }
        
        // Back leg - add inner parallel leg (offset toward inside/front)
        const backInnerLegGeo = new THREE.BoxGeometry(0.2, h, 0.2);
        const backInnerLeg = new THREE.Mesh(backInnerLegGeo, trussMaterial);
        backInnerLeg.position.set(xPos, h / 2, innerW/2 - legInnerOffset);
        backInnerLeg.name = 'building';
        scene.add(backInnerLeg);
        
        // Back leg horizontal connectors
        const numBackConnectors = Math.floor(h / legWebSpacing);
        
        // Add connector at bottom (ground level)
        const backBottomConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
        backBottomConnector.position.set(xPos, 0.1, innerW/2 - legInnerOffset/2);
        backBottomConnector.name = 'building';
        scene.add(backBottomConnector);
        
        // Add connector at top
        const backTopConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
        backTopConnector.position.set(xPos, h - 0.1, innerW/2 - legInnerOffset/2);
        backTopConnector.name = 'building';
        scene.add(backTopConnector);
        
        // Add intermediate connectors
        for (let c = 1; c < numBackConnectors; c++) {
          const connectorY = c * legWebSpacing;
          const backConnector = new THREE.Mesh(legConnectorGeo, trussMaterial);
          backConnector.position.set(xPos, connectorY, innerW/2 - legInnerOffset/2);
          backConnector.name = 'building';
          scene.add(backConnector);
        }
      }
      
      // === SIMPLE A-FRAME TRUSS (like reference image) ===
      if (!singleSlope) {
        // Rafter geometry - thicker rectangular steel tubing (top chord)
        const rafterGeo = new THREE.BoxGeometry(0.2, 0.25, rafterLength);
        
        // Left rafter (from front eave to ridge) - TOP CHORD
        const leftRafter = new THREE.Mesh(rafterGeo, trussMaterial);
        leftRafter.position.set(xPos, h + roofRise/2, -innerW/4);
        leftRafter.rotation.x = -rafterAngle;
        leftRafter.name = 'building';
        scene.add(leftRafter);
        
        // Right rafter (from back eave to ridge) - TOP CHORD
        const rightRafter = new THREE.Mesh(rafterGeo, trussMaterial);
        rightRafter.position.set(xPos, h + roofRise/2, innerW/4);
        rightRafter.rotation.x = rafterAngle;
        rightRafter.name = 'building';
        scene.add(rightRafter);
        
        // === WEB TRUSS - Skip for RV Cover and Carport (uses single trusses) ===
        const useSingleTruss = bStyle === 'rv_cover' || bStyle === 'carport';
        
        if (!useSingleTruss) {
          // Bottom chord (parallel to top chord, offset down by ~1 foot)
          const bottomChordOffset = 1.0; // 1 foot below top chord
          const bottomRafterGeo = new THREE.BoxGeometry(0.2, 0.2, rafterLength);
          
          // Left bottom chord
          const leftBottomChord = new THREE.Mesh(bottomRafterGeo, trussMaterial);
          leftBottomChord.position.set(xPos, h + roofRise/2 - bottomChordOffset, -innerW/4);
          leftBottomChord.rotation.x = -rafterAngle;
          leftBottomChord.name = 'building';
          scene.add(leftBottomChord);
          
          // Right bottom chord
          const rightBottomChord = new THREE.Mesh(bottomRafterGeo, trussMaterial);
          rightBottomChord.position.set(xPos, h + roofRise/2 - bottomChordOffset, innerW/4);
          rightBottomChord.rotation.x = rafterAngle;
          rightBottomChord.name = 'building';
          scene.add(rightBottomChord);
          
          // Vertical web members - 10 inch (0.83 ft) bars every 18 inches (1.5 ft)
          const webSpacing = 1.5; // 18 inches in feet
          const webBarGeo = new THREE.BoxGeometry(0.15, bottomChordOffset - 0.1, 0.15); // Vertical connector
          
          // Number of web members along each rafter
          const numWebs = Math.floor(rafterLength / webSpacing);
          
          for (let w = 1; w < numWebs; w++) {
            const progressAlongRafter = w * webSpacing / rafterLength;
            
            // Left side web members
            const leftWebZ = -innerW/2 + (progressAlongRafter * innerW/2);
            const leftWebY = h + (progressAlongRafter * roofRise) - bottomChordOffset/2;
            
            const leftWeb = new THREE.Mesh(webBarGeo, trussMaterial);
            leftWeb.position.set(xPos, leftWebY, leftWebZ);
            // Rotate to be perpendicular to the rafter angle
            leftWeb.rotation.x = -rafterAngle;
            leftWeb.name = 'building';
            scene.add(leftWeb);
            
            // Right side web members
            const rightWebZ = innerW/2 - (progressAlongRafter * innerW/2);
            const rightWebY = h + (progressAlongRafter * roofRise) - bottomChordOffset/2;
            
            const rightWeb = new THREE.Mesh(webBarGeo, trussMaterial);
            rightWeb.position.set(xPos, rightWebY, rightWebZ);
            rightWeb.rotation.x = rafterAngle;
            rightWeb.name = 'building';
            scene.add(rightWeb);
          }
          
          // Horizontal tie beam (crossbrace) - positioned 6 inches below peak
          const tieBeamHeight = h + roofRise - 0.5; // 6 inches (0.5 ft) below peak
          // Calculate width at this height based on roof angle
          const distanceFromPeak = 0.5; // 6 inches
          const tieBeamHalfWidth = distanceFromPeak / Math.tan(rafterAngle); // How wide at this height
          const tieBeamWidth = tieBeamHalfWidth * 2;
          const tieBeamGeo = new THREE.BoxGeometry(0.2, 0.2, tieBeamWidth);
          const tieBeam = new THREE.Mesh(tieBeamGeo, trussMaterial);
          tieBeam.position.set(xPos, tieBeamHeight, 0);
          tieBeam.name = 'building';
          scene.add(tieBeam);
        }
        
        // Diagonal knee braces - connecting leg to roof rafter
        // Start point: on the leg at 75% of wall height (higher up)
        const kneeStartY = h * 0.75;
        const kneeStartZ_front = -innerW/2; // At the front leg
        const kneeStartZ_back = innerW/2;   // At the back leg
        
        // End point: on the rafter, 2 feet along from the eave (shorter)
        const kneeHorizontalRun = 2; // How far along the rafter (horizontally)
        const kneeEndZ_front = -innerW/2 + kneeHorizontalRun;
        const kneeEndZ_back = innerW/2 - kneeHorizontalRun;
        // Calculate Y position on rafter at this point
        const rafterProgress = kneeHorizontalRun / (innerW/2); // 0 to 1 along rafter
        const kneeEndY = h + (rafterProgress * roofRise);
        
        // Calculate knee brace length and angle
        const kneeRise = kneeEndY - kneeStartY;
        const kneeRun = kneeHorizontalRun;
        const kneeLength = Math.sqrt(kneeRise ** 2 + kneeRun ** 2);
        const kneeAngle = Math.atan2(kneeRise, kneeRun);
        // Thicker knee braces
        const kneeGeo = new THREE.BoxGeometry(0.2, 0.2, kneeLength);
        
        // Front knee brace - from front leg to front rafter
        const frontKnee = new THREE.Mesh(kneeGeo, trussMaterial);
        frontKnee.position.set(
          xPos, 
          (kneeStartY + kneeEndY) / 2, 
          (kneeStartZ_front + kneeEndZ_front) / 2
        );
        frontKnee.rotation.x = -kneeAngle;
        frontKnee.name = 'building';
        scene.add(frontKnee);
        
        // Back knee brace - from back leg to back rafter
        const backKnee = new THREE.Mesh(kneeGeo, trussMaterial);
        backKnee.position.set(
          xPos, 
          (kneeStartY + kneeEndY) / 2, 
          (kneeStartZ_back + kneeEndZ_back) / 2
        );
        backKnee.rotation.x = kneeAngle;
        backKnee.name = 'building';
        scene.add(backKnee);
        
      } else {
        // Single slope - NO visible rafters (roof covers them)
        // Only add knee braces
        const kneeLength = 3.5;
        const kneeGeo = new THREE.BoxGeometry(0.12, 0.12, kneeLength);
        
        const frontPostZ = -innerW/2;
        const backPostZ = innerW/2;
        const frontPostTop = h + p * 2;
        const backPostTop = h;
        
        // Front knee brace - from post going UP toward rafter (+6 inches total)
        const frontBraceStartY = frontPostTop * 0.7 + 0.5;
        const frontBraceAngle = -Math.PI / 4;
        const frontKnee = new THREE.Mesh(kneeGeo, trussMaterial);
        frontKnee.position.set(
          xPos,
          frontBraceStartY + kneeLength/2 * Math.sin(Math.PI/4),
          frontPostZ + kneeLength/2 * Math.cos(Math.PI/4)
        );
        frontKnee.rotation.x = frontBraceAngle;
        frontKnee.name = 'building';
        scene.add(frontKnee);
        
        // Back knee brace - from post going UP toward rafter (+3 inches)
        const backBraceStartY = backPostTop * 0.7 + 0.25;
        const backBraceAngle = Math.PI / 4;
        const backKnee = new THREE.Mesh(kneeGeo, trussMaterial);
        backKnee.position.set(
          xPos,
          backBraceStartY + kneeLength/2 * Math.sin(Math.PI/4),
          backPostZ - kneeLength/2 * Math.cos(Math.PI/4)
        );
        backKnee.rotation.x = backBraceAngle;
        backKnee.name = 'building';
        scene.add(backKnee);
      }
    }

    // === CORNER LEG WEB TRUSS (for 40' to 50' template only) ===
    if (bStyle === 'carport_40_50') {
      const cornerLegWebSpacing = 1.5;
      const cornerLegInnerOffset = 0.8;
      
      cornerData.forEach(({ x, z, isFront, useHeight }) => {
        // Determine offset direction based on which side the leg is on
        const zOffset = isFront ? cornerLegInnerOffset : -cornerLegInnerOffset;
        
        // Inner parallel leg
        const innerLegGeo = new THREE.BoxGeometry(0.2, useHeight, 0.2);
        const innerLeg = new THREE.Mesh(innerLegGeo, trussMaterial);
        innerLeg.position.set(x, useHeight / 2, z + zOffset);
        innerLeg.name = 'building';
        scene.add(innerLeg);
        
        // Horizontal connectors (ladder rungs)
        const numConnectors = Math.floor(useHeight / cornerLegWebSpacing);
        const connectorGeo = new THREE.BoxGeometry(0.15, 0.15, cornerLegInnerOffset + 0.2);
        
        // Bottom connector
        const bottomConnector = new THREE.Mesh(connectorGeo, trussMaterial);
        bottomConnector.position.set(x, 0.1, z + zOffset/2);
        bottomConnector.name = 'building';
        scene.add(bottomConnector);
        
        // Top connector
        const topConnector = new THREE.Mesh(connectorGeo, trussMaterial);
        topConnector.position.set(x, useHeight - 0.1, z + zOffset/2);
        topConnector.name = 'building';
        scene.add(topConnector);
        
        // Intermediate connectors
        for (let c = 1; c < numConnectors; c++) {
          const connectorY = c * cornerLegWebSpacing;
          const connector = new THREE.Mesh(connectorGeo, trussMaterial);
          connector.position.set(x, connectorY, z + zOffset/2);
          connector.name = 'building';
          scene.add(connector);
        }
      });
    }

    // === HORIZONTAL TOP TRUSSES (spanning across front and back legs) ===
    // Only for 30' Carport and 40' to 50' Carport templates
    const is30or40_50Carport = bStyle === 'carport_30' || bStyle === 'carport_40_50';
    if (is30or40_50Carport && !singleSlope) {
      // Top truss beam spanning across all front legs at the top
      const topTrussLength = l - wallInset * 2; // Full length minus wall insets
      const topTrussGeo = new THREE.BoxGeometry(topTrussLength, 0.25, 0.2);
      
      // Front top truss - at the top of front legs
      const frontTopTruss = new THREE.Mesh(topTrussGeo, trussMaterial);
      frontTopTruss.position.set(0, h - 0.15, -innerW/2);
      frontTopTruss.name = 'building';
      scene.add(frontTopTruss);
      
      // Back top truss - at the top of back legs
      const backTopTruss = new THREE.Mesh(topTrussGeo, trussMaterial);
      backTopTruss.position.set(0, h - 0.15, innerW/2);
      backTopTruss.name = 'building';
      scene.add(backTopTruss);
    }
    
    // === BASE BEAMS (spanning across front and back legs at ground level) ===
    // Only for 40' to 50' Carport template - DOUBLE base rail
    if (bStyle === 'carport_40_50' && !singleSlope) {
      const baseTrussLength = l - wallInset * 2;
      const baseTrussGeo = new THREE.BoxGeometry(baseTrussLength, 0.15, 0.2);
      const baseRailOffset = 0.8; // Same as leg inner offset for alignment
      
      // Front double base rail
      const frontBaseBeam1 = new THREE.Mesh(baseTrussGeo, trussMaterial);
      frontBaseBeam1.position.set(0, 0.1, -innerW/2);
      frontBaseBeam1.name = 'building';
      scene.add(frontBaseBeam1);
      
      const frontBaseBeam2 = new THREE.Mesh(baseTrussGeo, trussMaterial);
      frontBaseBeam2.position.set(0, 0.1, -innerW/2 + baseRailOffset);
      frontBaseBeam2.name = 'building';
      scene.add(frontBaseBeam2);
      
      // Back double base rail
      const backBaseBeam1 = new THREE.Mesh(baseTrussGeo, trussMaterial);
      backBaseBeam1.position.set(0, 0.1, innerW/2);
      backBaseBeam1.name = 'building';
      scene.add(backBaseBeam1);
      
      const backBaseBeam2 = new THREE.Mesh(baseTrussGeo, trussMaterial);
      backBaseBeam2.position.set(0, 0.1, innerW/2 - baseRailOffset);
      backBaseBeam2.name = 'building';
      scene.add(backBaseBeam2);
    }

    // Add purlins (horizontal roof supports running lengthwise) - thicker
    const purlinGeo = new THREE.BoxGeometry(l - wallInset * 2, 0.15, 0.2);
    const purlinCount = 4; // Number of purlins per roof side
    
    if (!singleSlope) {
      // A-frame roof purlins
      for (let i = 1; i <= purlinCount; i++) {
        const t = i / (purlinCount + 1);
        const purlinZ = -innerW/2 * (1 - t);
        const purlinY = h + roofRise * t;
        
        // Left side purlins
        const leftPurlin = new THREE.Mesh(purlinGeo, trussMaterial);
        leftPurlin.position.set(0, purlinY, purlinZ);
        leftPurlin.name = 'building';
        scene.add(leftPurlin);
        
        // Right side purlins
        const rightPurlin = new THREE.Mesh(purlinGeo, trussMaterial);
        rightPurlin.position.set(0, purlinY, -purlinZ);
        rightPurlin.name = 'building';
        scene.add(rightPurlin);
      }
    }

    // Eave trim - horizontal trim at top of walls - 6 inches tall from roof down
    const trimHeight = 0.5; // 6 inches = 0.5 ft tall
    const trimThickness = 0.08; // Thin trim panel
    const eaveTrimGeo = new THREE.BoxGeometry(l, trimHeight, trimThickness);
    
    // For single slope, front (high side at -z) eave is at roof height
    const frontEaveHeight = singleSlope ? (h + p * 2) : h;
    
    // Front wall top trim (high side for single slope)
    const frontEave = new THREE.Mesh(eaveTrimGeo, trimMaterial);
    frontEave.position.set(0, frontEaveHeight - trimHeight/2, -w/2 - trimThickness/2);
    frontEave.name = 'building';
    scene.add(frontEave);
    
    // Back wall top trim (low side for single slope)
    const backEave = new THREE.Mesh(eaveTrimGeo, trimMaterial);
    backEave.position.set(0, h - trimHeight/2, w/2 + trimThickness/2);
    backEave.name = 'building';
    scene.add(backEave);
    
    // Left and Right end wall top trim - removed, keeping only roof-following gable trim
    
    // Exterior corner trim - 6 inches wide, full height of walls
    const cornerTrimGeoFront = new THREE.BoxGeometry(trimThickness, h, trimHeight);
    const cornerTrimGeoSide = new THREE.BoxGeometry(trimHeight, h, trimThickness);
    
    // Only add corner trim for non-carport buildings
    if (!isCarport) {
      // Front-left corner (L-shaped trim)
      const flCornerFront = new THREE.Mesh(cornerTrimGeoFront, trimMaterial);
      flCornerFront.position.set(-l/2 - trimThickness/2, h/2, -w/2 + trimHeight/2);
      flCornerFront.name = 'building';
      scene.add(flCornerFront);
      
      const flCornerSide = new THREE.Mesh(cornerTrimGeoSide, trimMaterial);
      flCornerSide.position.set(-l/2 + trimHeight/2, h/2, -w/2 - trimThickness/2);
      flCornerSide.name = 'building';
      scene.add(flCornerSide);
      
      // Front-right corner
      const frCornerFront = new THREE.Mesh(cornerTrimGeoFront, trimMaterial);
      frCornerFront.position.set(l/2 + trimThickness/2, h/2, -w/2 + trimHeight/2);
      frCornerFront.name = 'building';
      scene.add(frCornerFront);
      
      const frCornerSide = new THREE.Mesh(cornerTrimGeoSide, trimMaterial);
      frCornerSide.position.set(l/2 - trimHeight/2, h/2, -w/2 - trimThickness/2);
      frCornerSide.name = 'building';
      scene.add(frCornerSide);
      
      // Back-left corner
      const blCornerFront = new THREE.Mesh(cornerTrimGeoFront, trimMaterial);
      blCornerFront.position.set(-l/2 - trimThickness/2, h/2, w/2 - trimHeight/2);
      blCornerFront.name = 'building';
      scene.add(blCornerFront);
      
      const blCornerSide = new THREE.Mesh(cornerTrimGeoSide, trimMaterial);
      blCornerSide.position.set(-l/2 + trimHeight/2, h/2, w/2 + trimThickness/2);
      blCornerSide.name = 'building';
      scene.add(blCornerSide);
      
      // Back-right corner
      const brCornerFront = new THREE.Mesh(cornerTrimGeoFront, trimMaterial);
      brCornerFront.position.set(l/2 + trimThickness/2, h/2, w/2 - trimHeight/2);
      brCornerFront.name = 'building';
      scene.add(brCornerFront);
      
      const brCornerSide = new THREE.Mesh(cornerTrimGeoSide, trimMaterial);
      brCornerSide.position.set(l/2 - trimHeight/2, h/2, w/2 + trimThickness/2);
      brCornerSide.name = 'building';
      scene.add(brCornerSide);
    }
    
    // Gable end trim - follows roof angle, 6 inches tall hanging down from roof edge
    const gableDrop = 0.208; // 2.5 inches drop from roof
    if (!singleSlope) {
      // A-frame gable - two angled pieces on each end
      const roofAngleTrim = Math.atan2(p, w/2);
      const gableTrimLength = Math.sqrt((w/2) ** 2 + p ** 2);
      const gableTrimGeo = new THREE.BoxGeometry(trimThickness, trimHeight, gableTrimLength);
      
      // Left end - front slope
      const leftGableFront = new THREE.Mesh(gableTrimGeo, trimMaterial);
      leftGableFront.position.set(-l/2 - trimThickness/2, h + p/2 - gableDrop, -w/4);
      leftGableFront.rotation.x = -roofAngleTrim;
      leftGableFront.name = 'building';
      scene.add(leftGableFront);
      
      // Left end - back slope
      const leftGableBack = new THREE.Mesh(gableTrimGeo, trimMaterial);
      leftGableBack.position.set(-l/2 - trimThickness/2, h + p/2 - gableDrop, w/4);
      leftGableBack.rotation.x = roofAngleTrim;
      leftGableBack.name = 'building';
      scene.add(leftGableBack);
      
      // Right end - front slope
      const rightGableFront = new THREE.Mesh(gableTrimGeo, trimMaterial);
      rightGableFront.position.set(l/2 + trimThickness/2, h + p/2 - gableDrop, -w/4);
      rightGableFront.rotation.x = -roofAngleTrim;
      rightGableFront.name = 'building';
      scene.add(rightGableFront);
      
      // Right end - back slope
      const rightGableBack = new THREE.Mesh(gableTrimGeo, trimMaterial);
      rightGableBack.position.set(l/2 + trimThickness/2, h + p/2 - gableDrop, w/4);
      rightGableBack.rotation.x = roofAngleTrim;
      rightGableBack.name = 'building';
      scene.add(rightGableBack);
    } else {
      // Single slope - one angled piece on each end following roof slope
      const slopeAngle = Math.atan2(p * 2, w);
      const slopeTrimLength = Math.sqrt(w ** 2 + (p * 2) ** 2);
      const slopeTrimGeo = new THREE.BoxGeometry(trimThickness, trimHeight, slopeTrimLength);
      
      const leftSlopeTrim = new THREE.Mesh(slopeTrimGeo, trimMaterial);
      leftSlopeTrim.position.set(-l/2 - trimThickness/2, h + p - gableDrop, 0);
      leftSlopeTrim.rotation.x = slopeAngle;
      leftSlopeTrim.name = 'building';
      scene.add(leftSlopeTrim);
      
      const rightSlopeTrim = new THREE.Mesh(slopeTrimGeo, trimMaterial);
      rightSlopeTrim.position.set(l/2 + trimThickness/2, h + p - gableDrop, 0);
      rightSlopeTrim.rotation.x = slopeAngle;
      rightSlopeTrim.name = 'building';
      scene.add(rightSlopeTrim);
    }

    // Add side labels (Front, Back, Left End, Right End)
    const createTextSprite = (text) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 64;
      
      context.fillStyle = 'rgba(0, 0, 0, 0.7)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      context.font = 'Bold 32px Arial';
      context.fillStyle = 'white';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, canvas.width / 2, canvas.height / 2);
      
      const texture = new THREE.CanvasTexture(canvas);
      const material = new THREE.SpriteMaterial({ map: texture });
      const sprite = new THREE.Sprite(material);
      sprite.scale.set(4, 1, 1);
      sprite.name = 'building';
      return sprite;
    };
    
    // Left label (-Z direction)
    const leftLabel = createTextSprite('LEFT');
    leftLabel.position.set(0, 1, -w/2 - 3);
    scene.add(leftLabel);
    
    // Right label (+Z direction)
    const rightLabel = createTextSprite('RIGHT');
    rightLabel.position.set(0, 1, w/2 + 3);
    scene.add(rightLabel);
    
    // Front label (-X direction)
    const frontLabel = createTextSprite('FRONT');
    frontLabel.position.set(-l/2 - 3, 1, 0);
    scene.add(frontLabel);
    
    // Back label (+X direction)
    const backLabel = createTextSprite('BACK');
    backLabel.position.set(l/2 + 3, 1, 0);
    scene.add(backLabel);

    // === LEAN-TOS ===
    // Lean-tos extend from the front (-Z) or back (+Z) sides of the building
    // lLean = left lean (front side, -Z), rLean = right lean (back side, +Z)
    // LEAN-TO IS BACK-JUSTIFIED: starts at back of main building (positive X), extends toward front
    
    const buildLeanTo = (side, leanType, leanWidth, leanLength, leanHeight) => {
      if (leanType === 'none') return;
      
      const isLeftLean = side === 'left'; // Front side (-Z)
      const zDirection = isLeftLean ? -1 : 1;
      const leanZ = isLeftLean ? -w/2 - leanWidth/2 : w/2 + leanWidth/2;
      
      // Lean-to post height from config
      const leanPostHeight = leanHeight;
      
      // Beam sits at top of posts
      const beamHeight = leanPostHeight;
      
      // Roof LOW side sits ON TOP of the beam, HIGH at outer edge
      // Use 3/12 pitch: roof slopes UP toward outer edge
      const roofPitch = 3 / 12;
      const roofDrop = leanWidth * roofPitch;
      const mainBuildingAttachY = beamHeight + 0.2; // LOW side sits on top of beam
      const outerRoofY = mainBuildingAttachY + roofDrop; // HIGH side at outer edge
      
      // Lean-to roof length based on config (or match main building length)
      const roofLength = leanLength + 2; // Add overhang
      
      // BACK-JUSTIFIED: lean-to starts at back of building (X = l/2) and extends toward front
      // Back end is at X = l/2, front end is at X = l/2 - leanLength
      const leanBackX = l / 2;  // Aligned with back of main building
      const leanFrontX = l / 2 - leanLength;  // Front edge of lean-to
      const leanCenterX = (leanBackX + leanFrontX) / 2;  // Center for positioning elements
      
      // Lean-to posts (legs)
      const leanPostGeo = new THREE.BoxGeometry(0.25, leanPostHeight, 0.25);
      const leanPostMat = new THREE.MeshStandardMaterial({
        color: '#B5B5B3',
        metalness: 0.3,
        roughness: 0.5
      });
      
      // Posts at corners of lean-to (outer edge) - back-justified
      const postPositions = [
        [leanFrontX + 0.3, leanZ + zDirection * (leanWidth/2 - 0.3)],  // Front corner
        [leanBackX - 0.3, leanZ + zDirection * (leanWidth/2 - 0.3)]    // Back corner
      ];
      
      // Add intermediate posts
      const postSpacing = 5;
      const numIntermediatePosts = Math.floor(leanLength / postSpacing) - 1;
      for (let i = 1; i <= numIntermediatePosts; i++) {
        const xPos = leanFrontX + i * (leanLength / (numIntermediatePosts + 1));
        postPositions.push([xPos, leanZ + zDirection * (leanWidth/2 - 0.3)]);
      }
      
      postPositions.forEach(([x, z]) => {
        const post = new THREE.Mesh(leanPostGeo, leanPostMat);
        post.position.set(x, leanPostHeight / 2, z);
        post.castShadow = true;
        post.name = 'building';
        scene.add(post);
      });
      
      // Horizontal beam at TOP of posts - match wall length to hide behind trim
      const beamGeo = new THREE.BoxGeometry(leanLength, 0.2, 0.15);
      const beam = new THREE.Mesh(beamGeo, leanPostMat);
      beam.position.set(leanCenterX, beamHeight, leanZ + zDirection * (leanWidth/2 - 0.3));
      beam.name = 'building';
      scene.add(beam);
      
      // Lean-to roof - HIGH at outer beam, slopes DOWN toward main building
      const roofWidth = leanWidth + 0.5;
      const roofRise = outerRoofY - mainBuildingAttachY; // Positive = high at outer edge
      const roofAngle = Math.atan2(roofRise, roofWidth);
      const roofPanelLength = Math.sqrt(roofWidth ** 2 + roofRise ** 2);
      
      const leanRoofGeo = new THREE.BoxGeometry(roofLength, 0.05, roofPanelLength);
      // Use same roof texture - ribs direction matches main roof
      const leanRoofMat = roofMaterial.clone();
      const leanRoofTexture = roofPanelTexture.clone();
      leanRoofTexture.wrapS = THREE.RepeatWrapping;
      leanRoofTexture.wrapT = THREE.RepeatWrapping;
      if (ribDir === 'horizontal') {
        // Ribs run along the length (horizontal)
        leanRoofTexture.rotation = Math.PI / 2;
        leanRoofTexture.center.set(0.5, 0.5);
        leanRoofTexture.repeat.set(roofPanelLength / 2, leanLength / 2);
      } else {
        // Ribs run down the slope (vertical) - DEFAULT
        leanRoofTexture.repeat.set(leanLength / 2, roofPanelLength / 2);
      }
      leanRoofMat.map = leanRoofTexture;
      
      const leanRoof = new THREE.Mesh(leanRoofGeo, leanRoofMat);
      
      // Position roof - centered on lean-to (back-justified) with 3" overhang
      const roofCenterY = (mainBuildingAttachY + outerRoofY) / 2;
      const roofCenterZ = isLeftLean ? -w/2 - roofWidth/2 : w/2 + roofWidth/2; // 3" overhang
      
      leanRoof.position.set(leanCenterX, roofCenterY, roofCenterZ);
      // Slope DOWN toward main building (high at outer edge)
      leanRoof.rotation.x = isLeftLean ? -roofAngle : roofAngle;
      leanRoof.castShadow = true;
      leanRoof.receiveShadow = true;
      leanRoof.name = 'building';
      scene.add(leanRoof);
      
      // Rafters for lean-to - sitting under the roof (back-justified)
      const rafterSpacing = 5;
      const numRafters = Math.floor(leanLength / rafterSpacing) + 1;
      const rafterLength = Math.sqrt(leanWidth ** 2 + roofRise ** 2);
      const rafterGeo = new THREE.BoxGeometry(0.15, 0.2, rafterLength);
      
      for (let i = 0; i <= numRafters; i++) {
        const xPos = leanFrontX + 0.5 + i * ((leanLength - 1) / numRafters);
        const rafter = new THREE.Mesh(rafterGeo, leanPostMat);
        
        const rafterCenterY = (mainBuildingAttachY + outerRoofY) / 2 - 0.15;
        const rafterCenterZ = isLeftLean ? -w/2 - leanWidth/2 + 0.3 : w/2 + leanWidth/2 - 0.3;
        
        rafter.position.set(xPos, rafterCenterY, rafterCenterZ);
        // Slope DOWN toward main building
        rafter.rotation.x = isLeftLean ? -roofAngle : roofAngle;
        rafter.name = 'building';
        scene.add(rafter);
      }
      
      // Add top-3' panel on the OUTER wall of the lean-to (applies to all lean types)
      const leanPanelHeight = 3; // 3-foot top panel
      const leanPanelGeo = new THREE.BoxGeometry(leanLength, leanPanelHeight, 0.1);
      const leanPanelMat = new THREE.MeshStandardMaterial({
        color: wc,
        metalness: 0.4,
        roughness: 0.5,
        side: THREE.DoubleSide
      });
      const leanOuterPanel = new THREE.Mesh(leanPanelGeo, leanPanelMat);
      // Position at top of outer wall (just below the beam)
      const panelY = leanPostHeight - leanPanelHeight / 2;
      const panelZ = isLeftLean ? -w/2 - leanWidth + 0.05 : w/2 + leanWidth - 0.05;
      leanOuterPanel.position.set(leanCenterX, panelY, panelZ);
      leanOuterPanel.castShadow = true;
      leanOuterPanel.receiveShadow = true;
      leanOuterPanel.name = 'building';
      scene.add(leanOuterPanel);
      
      // === LEAN-TO TRIM ===
      // Trim material - uses trim color (tc) which user can customize
      const leanTrimMat = new THREE.MeshStandardMaterial({
        color: tc,
        metalness: 0.3,
        roughness: 0.5
      });
      
      // 1. HORIZONTAL TRIM - Along the outer roof edge (top fascia)
      const outerRoofTrimGeo = new THREE.BoxGeometry(leanLength, 0.5, 0.15); // Match sidewall length
      const outerRoofTrim = new THREE.Mesh(outerRoofTrimGeo, leanTrimMat.clone());
      const outerEdgeZ = isLeftLean ? -w/2 - leanWidth - 0.1 : w/2 + leanWidth + 0.1; // In front of sidewall
      outerRoofTrim.position.set(leanCenterX, outerRoofY - 3.25, outerEdgeZ);
      outerRoofTrim.castShadow = true;
      outerRoofTrim.name = 'building';
      scene.add(outerRoofTrim);
      
      // 2. SLOPED GABLE END TRIM - On the exposed gable ends following roof pitch
      // This is the fascia that runs along the sloped edge of the roof at front/back
      const gableTrimGeo = new THREE.BoxGeometry(0.15, 0.5, roofPanelLength - 0.5); // Length to meet sidewall trim
      
      // Front gable trim - flush under roof
      const frontGableTrim = new THREE.Mesh(gableTrimGeo, leanTrimMat.clone());
      // Position flush under roof, meeting sidewall trim
      const gableTrimZ = isLeftLean ? roofCenterZ + 0.25 : roofCenterZ - 0.25;
      frontGableTrim.position.set(leanFrontX, roofCenterY - 0.19, gableTrimZ);
      frontGableTrim.rotation.x = isLeftLean ? -roofAngle : roofAngle;
      frontGableTrim.castShadow = true;
      frontGableTrim.name = 'building';
      scene.add(frontGableTrim);
      
      // Back gable trim - flush under roof
      const backGableTrim = new THREE.Mesh(gableTrimGeo.clone(), leanTrimMat.clone());
      backGableTrim.position.set(leanBackX, roofCenterY - 0.19, gableTrimZ);
      backGableTrim.rotation.x = isLeftLean ? -roofAngle : roofAngle;
      backGableTrim.castShadow = true;
      backGableTrim.name = 'building';
      scene.add(backGableTrim);
      
      // If lean with storage, add 5-foot enclosed storage section at BACK END ONLY
      if (leanType === 'lean_storage') {
        const storageDepth = 5; // Fixed 5-foot storage section at BACK end only
        
        const storageMat = new THREE.MeshStandardMaterial({
          color: wc,
          metalness: 0.4,
          roughness: 0.5,
          side: THREE.DoubleSide
        });
        
        // Z coordinates - lean extends from main building wall outward
        const zInner = isLeftLean ? -w/2 : w/2;  // Against main building
        const zOuter = isLeftLean ? -w/2 - leanWidth : w/2 + leanWidth;  // Outer edge
        const zCenter = (zInner + zOuter) / 2;  // Center of lean-to for end walls
        const gableHeight = outerRoofY - leanPostHeight;
        
        // === BACK END STORAGE SECTION ONLY (5 feet at back end) ===
        // Back end is at leanBackX (aligned with back of main building)
        const backStorageX = leanBackX - storageDepth/2;
        
        // Back storage - outer wall (5ft section at outer edge)
        const backOuterWallGeo = new THREE.BoxGeometry(storageDepth, leanPostHeight, 0.1);
        const backOuterWall = new THREE.Mesh(backOuterWallGeo, storageMat.clone());
        backOuterWall.position.set(backStorageX, leanPostHeight / 2, zOuter);
        backOuterWall.castShadow = true;
        backOuterWall.receiveShadow = true;
        backOuterWall.name = 'building';
        scene.add(backOuterWall);
        
        // Back storage - end wall (at the very back, spans full lean-to width)
        const endWallGeo = new THREE.BoxGeometry(0.1, leanPostHeight, leanWidth);
        const backEndWall = new THREE.Mesh(endWallGeo, storageMat.clone());
        backEndWall.position.set(leanBackX, leanPostHeight / 2, zCenter);
        backEndWall.castShadow = true;
        backEndWall.receiveShadow = true;
        backEndWall.name = 'building';
        scene.add(backEndWall);
        
        // Back storage - interior partition wall (separating storage from open area)
        const backPartitionWall = new THREE.Mesh(endWallGeo.clone(), storageMat.clone());
        backPartitionWall.position.set(leanBackX - storageDepth, leanPostHeight / 2, zCenter);
        backPartitionWall.castShadow = true;
        backPartitionWall.receiveShadow = true;
        backPartitionWall.name = 'building';
        scene.add(backPartitionWall);
        
        // Back storage - gable (triangular top at the back end)
        const backGableGeo = new THREE.BufferGeometry();
        const backVertices = new Float32Array([
          leanBackX, leanPostHeight, zInner,
          leanBackX, leanPostHeight, zOuter,
          leanBackX, leanPostHeight + gableHeight, zInner
        ]);
        backGableGeo.setAttribute('position', new THREE.BufferAttribute(backVertices, 3));
        backGableGeo.computeVertexNormals();
        const backGable = new THREE.Mesh(backGableGeo, storageMat.clone());
        backGable.name = 'building';
        scene.add(backGable);
      }
      
      // Add lean-to label - positioned at center of lean-to (back-justified)
      const leanLabel = createTextSprite(isLeftLean ? 'LEFT LEAN' : 'RIGHT LEAN');
      leanLabel.position.set(leanCenterX, 1, leanZ + zDirection * (leanWidth/2 + 2));
      scene.add(leanLabel);
    };
    
    // Build lean-tos based on config
    buildLeanTo('left', lLean, lLeanW, lLeanL, lLeanH);   // Left Lean option -> Front side (-Z)
    buildLeanTo('right', rLean, rLeanW, rLeanL, rLeanH);  // Right Lean option -> Back side (+Z)

  }, []);

  // Initialize scene
  useEffect(() => {
    const container = containerRef.current;
    const three = threeRef.current;
    
    if (!container || three.initialized) return;
    three.initialized = true;

    // Scene
    const scene = new THREE.Scene();
    three.scene = scene;

    // Load HDRI environment
    new RGBELoader()
      .setPath('/')
      .load('hdri.hdr', (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;
        scene.background = texture;
        scene.environment = texture;
      }, undefined, () => {
        // Fallback to solid background if HDRI fails
        scene.background = new THREE.Color(0x87CEEB);
      });

    // Add fog
    scene.fog = new THREE.Fog(0x9ad9ea, 100, 500);

    // Camera - positioned based on building style
    const containerW = container.clientWidth;
    const containerH = container.clientHeight;
    const camera = new THREE.PerspectiveCamera(50, containerW / containerH, 0.1, 2000);
    
    // Set camera position based on building style
    const isGarage = buildingStyle === 'garage';
    const isCarport = buildingStyle === 'carport';
    const is30Carport = buildingStyle === '30_carport';
    const is40to50Carport = buildingStyle === '40_to_50_carport';
    
    if (isGarage) {
      // Diagonal view showing front (leftEnd with garage doors) and right side (backSide with walk door)
      camera.position.set(-50, 18, 45);
    } else if (isCarport || is40to50Carport) {
      // Left front diagonal view for carports
      camera.position.set(-45, 18, -40);
    } else if (is30Carport) {
      // Front right diagonal view for 30' carport
      camera.position.set(-45, 18, 40);
    } else {
      // Default front view: looking from -Z toward +Z
      camera.position.set(0, 20, -50);
    }
    three.camera = camera;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(containerW, containerH);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    container.appendChild(renderer.domElement);
    three.renderer = renderer;

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxDistance = 150;
    controls.minDistance = 10;
    controls.maxPolarAngle = Math.PI / 2.1;
    controls.target.set(0, legHeight / 2, 0);
    three.controls = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    scene.add(new THREE.HemisphereLight(0xffffbb, 0x080820, 0.5));

    const sun = new THREE.DirectionalLight(0xffffff, 1.0);
    sun.position.set(50, 80, -30);
    sun.castShadow = true;
    sun.shadow.mapSize.width = 2048;
    sun.shadow.mapSize.height = 2048;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 200;
    sun.shadow.camera.left = -50;
    sun.shadow.camera.right = 50;
    sun.shadow.camera.top = 50;
    sun.shadow.camera.bottom = -50;
    scene.add(sun);

    // Ground (grass)
    const grassTexture = new THREE.TextureLoader().load('/grass.jpg');
    grassTexture.wrapS = THREE.RepeatWrapping;
    grassTexture.wrapT = THREE.RepeatWrapping;
    grassTexture.repeat.set(25, 25);
    
    const groundGeo = new THREE.PlaneGeometry(400, 400);
    const groundMat = new THREE.MeshStandardMaterial({
      map: grassTexture,
      color: 0x2d5a27,
      roughness: 0.9
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.3;
    ground.receiveShadow = true;
    ground.name = 'permanent';
    scene.add(ground);

    // Logo watermarks on the ground (at the base of the building on each side)
    const logoTexture = new THREE.TextureLoader().load('/logo.png');
    logoTexture.colorSpace = THREE.SRGBColorSpace;
    
    // Logo is 970x273 pixels - aspect ratio ~3.55:1
    const logoWidth = 14; // Width in feet
    const logoHeight = logoWidth / 3.55; // Maintain aspect ratio (~3.9 feet)
    const logoGeo = new THREE.PlaneGeometry(logoWidth, logoHeight);
    const logoMat = new THREE.MeshBasicMaterial({
      map: logoTexture,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      side: THREE.DoubleSide
    });
    
    // Position logos right at the base of the building (touching the concrete slab)
    const halfLength = length / 2 + 1; // Half building length + tiny offset
    const halfWidth = width / 2 + 1;   // Half building width + tiny offset
    const logoY = -0.2; // Slightly above ground
    
    // Front logo (negative Z - at front side base) - facing outward (toward viewer)
    const logoFront = new THREE.Mesh(logoGeo, logoMat);
    logoFront.rotation.x = -Math.PI / 2;
    logoFront.rotation.z = Math.PI; // Rotated to face outward
    logoFront.position.set(0, logoY, -(halfWidth + logoHeight/2));
    logoFront.name = 'permanent';
    scene.add(logoFront);
    
    // Back logo (positive Z - at back side base) - facing outward
    const logoBack = new THREE.Mesh(logoGeo, logoMat);
    logoBack.rotation.x = -Math.PI / 2;
    logoBack.rotation.z = 0; // Faces outward (away from building)
    logoBack.position.set(0, logoY, halfWidth + logoHeight/2);
    logoBack.name = 'permanent';
    scene.add(logoBack);
    
    // Left logo (negative X - at left end base) - facing outward
    const logoLeft = new THREE.Mesh(logoGeo, logoMat);
    logoLeft.rotation.x = -Math.PI / 2;
    logoLeft.rotation.z = -Math.PI / 2; // Rotated to face outward
    logoLeft.position.set(-(halfLength + logoHeight/2), logoY, 0);
    logoLeft.name = 'permanent';
    scene.add(logoLeft);
    
    // Right logo (positive X - at right end base) - facing outward
    const logoRight = new THREE.Mesh(logoGeo, logoMat);
    logoRight.rotation.x = -Math.PI / 2;
    logoRight.rotation.z = Math.PI / 2; // Rotated to face outward
    logoRight.position.set(halfLength + logoHeight/2, logoY, 0);
    logoRight.name = 'permanent';
    scene.add(logoRight);

    // Build initial structure
    buildBuilding(width, length, legHeight, roofPitch, roofColor, trimColor, wallColor, wainscotColor, leftEnd, rightEnd, frontSide, backSide, isSingleSlope, sidingOrientation, garageDoors, walkDoors, windows, enclosedLength, enclosedSide, leftLean, rightLean, buildingStyle, leftLeanWidth, leftLeanLength, leftLeanHeight, rightLeanWidth, rightLeanLength, rightLeanHeight, roofRibDirection);

    // Animation loop
    const animate = () => {
      three.frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize handler
    const onResize = () => {
      if (!container) return;
      const nw = container.clientWidth;
      const nh = container.clientHeight;
      camera.aspect = nw / nh;
      camera.updateProjectionMatrix();
      renderer.setSize(nw, nh);
    };
    window.addEventListener('resize', onResize);

    // Helper to create/update selection box
    const updateSelectionBox = (group) => {
      // Remove existing selection box
      if (three.selectionBox) {
        scene.remove(three.selectionBox);
        three.selectionBox.dispose();
        three.selectionBox = null;
      }
      
      if (!group) return;
      
      // Create BoxHelper for selection visualization
      const box = new THREE.BoxHelper(group, 0x00ff00);
      box.name = 'selectionBox';
      scene.add(box);
      three.selectionBox = box;
    };
    
    // Helper to calculate measurements - uses userData from the 3D object directly
    const calculateMeasurements = (group, wallWidth) => {
      const wall = group.userData.wall;
      let currentPos;
      
      if (wall === 'leftEnd' || wall === 'rightEnd') {
        currentPos = group.position.z;
      } else {
        currentPos = group.position.x;
      }
      
      // Calculate distances from wall edges
      const leftDist = currentPos + wallWidth / 2;
      const rightDist = wallWidth / 2 - currentPos;
      
      // Get item dimensions directly from userData (stored when creating the 3D object)
      const itemWidth = group.userData.itemWidth || 0;
      const itemHeight = group.userData.itemHeight || 0;
      
      return {
        leftDist: Math.max(0, leftDist - itemWidth/2).toFixed(1),
        rightDist: Math.max(0, rightDist - itemWidth/2).toFixed(1),
        width: itemWidth,
        height: itemHeight
      };
    };
    
    // Drag and drop handlers for doors/windows
    const onMouseDown = (event) => {
      if (event.button !== 0) return; // Only left click
      
      const rect = renderer.domElement.getBoundingClientRect();
      three.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      three.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      three.raycaster.setFromCamera(three.mouse, camera);
      
      // Check for intersections with draggable items (and their children)
      const allDraggableMeshes = [];
      three.draggables.forEach(group => {
        group.traverse(child => {
          if (child.isMesh) {
            allDraggableMeshes.push(child);
          }
        });
      });
      
      const intersects = three.raycaster.intersectObjects(allDraggableMeshes, false);
      
      if (intersects.length > 0) {
        // Find the parent group (draggable container)
        let draggableGroup = intersects[0].object;
        while (draggableGroup && draggableGroup.name !== 'draggable') {
          draggableGroup = draggableGroup.parent;
        }
        
        if (draggableGroup && draggableGroup.userData && draggableGroup.userData.id) {
          controls.enabled = false; // Disable orbit controls while dragging
          
          const { id, itemType, wall, wallWidth, type } = draggableGroup.userData;
          
          // Calculate current position percentage
          let positionPercent;
          if (wall === 'leftEnd' || wall === 'rightEnd') {
            positionPercent = ((draggableGroup.position.z + wallWidth / 2) / wallWidth) * 100;
          } else {
            positionPercent = ((draggableGroup.position.x + wallWidth / 2) / wallWidth) * 100;
          }
          
          // Get item dimensions directly from userData (stored in 3D object)
          const itemWidth = draggableGroup.userData.itemWidth;
          const itemHeight = draggableGroup.userData.itemHeight;
          
          // Set selected item
          setSelectedItem({
            id,
            type,
            itemType,
            wall,
            position: Math.round(positionPercent),
            dimensions: { width: itemWidth, height: itemHeight }
          });
          
          // Update selection box
          updateSelectionBox(draggableGroup);
          
          // Calculate initial measurements
          const meas = calculateMeasurements(draggableGroup, wallWidth);
          setMeasurements(meas);
          
          // Show edit popup after a short delay (for click vs drag detection)
          const clickStartTime = Date.now();
          
          three.dragState = {
            group: draggableGroup,
            itemType,
            itemId: id,
            wall,
            wallWidth,
            startMouseX: event.clientX,
            clickStartTime,
            hasMoved: false
          };
          
          // Set popup position
          setPopupPosition({ x: event.clientX, y: event.clientY });
          
          renderer.domElement.style.cursor = 'grabbing';
          setIsDragging(true);
        }
      } else {
        // Clicked on empty space - deselect
        setSelectedItem(null);
        setShowEditPopup(false);
        setShowResizeMenu(false);
        setMeasurements(null);
        updateSelectionBox(null);
      }
    };
    
    const onMouseMove = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      three.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      three.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Check if hovering over a draggable (when not already dragging)
      if (!three.dragState) {
        three.raycaster.setFromCamera(three.mouse, camera);
        
        // Collect all meshes from draggable groups
        const allDraggableMeshes = [];
        three.draggables.forEach(group => {
          group.traverse(child => {
            if (child.isMesh) {
              allDraggableMeshes.push(child);
            }
          });
        });
        
        const intersects = three.raycaster.intersectObjects(allDraggableMeshes, false);
        renderer.domElement.style.cursor = intersects.length > 0 ? 'grab' : 'default';
        return;
      }
      
      // Mark as moved if mouse moved significantly
      const moveThreshold = 5;
      if (Math.abs(event.clientX - three.dragState.startMouseX) > moveThreshold) {
        three.dragState.hasMoved = true;
        setShowEditPopup(false); // Hide popup while dragging
      }
      
      // Handle dragging
      const { wall, wallWidth, startMouseX, group } = three.dragState;
      const deltaX = event.clientX - startMouseX;
      
      // Convert pixel delta to 3D world units (approximate)
      const dragSensitivity = 0.05; // Adjust for feel
      let newLocalX;
      
      // Get camera position to determine drag direction based on view angle
      const cameraPos = camera.position.clone();
      
      // Calculate new position based on wall orientation and camera view
      if (wall === 'leftEnd' || wall === 'rightEnd') {
        // For end walls, dragging moves in Z direction
        // Determine direction multiplier based on camera position and wall
        let dirMultiplier = 1;
        
        if (wall === 'leftEnd') {
          // leftEnd wall is at negative X, faces outward (negative X direction)
          // If camera is on negative X side (looking at front of wall), standard direction
          // If camera is on positive X side (looking at back), invert
          dirMultiplier = cameraPos.x < 0 ? 1 : -1;
        } else {
          // rightEnd wall is at positive X, faces outward (positive X direction)
          // If camera is on positive X side, standard direction
          // If camera is on negative X side, invert
          dirMultiplier = cameraPos.x > 0 ? 1 : -1;
        }
        
        newLocalX = group.position.z + deltaX * dragSensitivity * dirMultiplier;
        // Clamp to wall bounds (10% - 90% of wall width)
        const minX = -wallWidth / 2 + wallWidth * 0.1;
        const maxX = wallWidth / 2 - wallWidth * 0.1;
        newLocalX = Math.max(minX, Math.min(maxX, newLocalX));
        group.position.z = newLocalX;
      } else {
        // For side walls (front/back), dragging moves in X direction
        // Determine direction multiplier based on camera position and wall
        let dirMultiplier = 1;
        
        if (wall === 'frontSide') {
          // frontSide wall is at negative Z, faces outward (negative Z direction)
          // If camera is on negative Z side, standard direction
          // If camera is on positive Z side, invert
          dirMultiplier = cameraPos.z < 0 ? 1 : -1;
        } else {
          // backSide wall is at positive Z, faces outward (positive Z direction)
          // If camera is on positive Z side, standard direction
          // If camera is on negative Z side, invert
          dirMultiplier = cameraPos.z > 0 ? 1 : -1;
        }
        
        newLocalX = group.position.x + deltaX * dragSensitivity * dirMultiplier;
        const minX = -wallWidth / 2 + wallWidth * 0.1;
        const maxX = wallWidth / 2 - wallWidth * 0.1;
        newLocalX = Math.max(minX, Math.min(maxX, newLocalX));
        group.position.x = newLocalX;
      }
      
      // Update selection box position
      if (three.selectionBox) {
        three.selectionBox.update();
      }
      
      // Update measurements during drag
      const meas = calculateMeasurements(group, wallWidth);
      setMeasurements(meas);
      
      three.dragState.startMouseX = event.clientX;
      renderer.domElement.style.cursor = 'grabbing';
    };
    
    const onMouseUp = (event) => {
      if (!three.dragState) return;
      
      const { itemType, itemId, wall, wallWidth, group, clickStartTime, hasMoved } = three.dragState;
      
      // Calculate new position percentage
      let positionPercent;
      if (wall === 'leftEnd' || wall === 'rightEnd') {
        positionPercent = ((group.position.z + wallWidth / 2) / wallWidth) * 100;
      } else {
        positionPercent = ((group.position.x + wallWidth / 2) / wallWidth) * 100;
      }
      
      // Clamp to valid range
      positionPercent = Math.max(10, Math.min(90, positionPercent));
      
      // Call the callback to update the state
      if (onUpdateItemPosition && hasMoved) {
        onUpdateItemPosition(itemType, itemId, Math.round(positionPercent));
      }
      
      // If it was a click (not a drag), show the edit popup
      const clickDuration = Date.now() - clickStartTime;
      if (!hasMoved && clickDuration < 300) {
        setPopupPosition({ x: event.clientX, y: event.clientY });
        setShowEditPopup(true);
      }
      
      controls.enabled = true;
      three.dragState = null;
      setIsDragging(false);
    };
    
    renderer.domElement.addEventListener('mousedown', onMouseDown);
    renderer.domElement.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('mouseup', onMouseUp);
    renderer.domElement.addEventListener('mouseleave', onMouseUp);

    // Cleanup
    return () => {
      window.removeEventListener('resize', onResize);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      renderer.domElement.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('mouseleave', onMouseUp);
      cancelAnimationFrame(three.frameId);
      controls.dispose();
      renderer.dispose();
      three.initialized = false;
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [buildBuilding, width, length, legHeight, roofPitch, roofColor, trimColor, wallColor, wainscotColor, leftEnd, rightEnd, frontSide, backSide, isSingleSlope, sidingOrientation, enclosedLength, enclosedSide, leftLean, rightLean, buildingStyle, onUpdateItemPosition, roofRibDirection]);

  // Rebuild when config changes (including doors/windows)
  useEffect(() => {
    const three = threeRef.current;
    if (three.initialized && three.scene) {
      buildBuilding(width, length, legHeight, roofPitch, roofColor, trimColor, wallColor, wainscotColor, leftEnd, rightEnd, frontSide, backSide, isSingleSlope, sidingOrientation, garageDoors, walkDoors, windows, enclosedLength, enclosedSide, leftLean, rightLean, buildingStyle, leftLeanWidth, leftLeanLength, leftLeanHeight, rightLeanWidth, rightLeanLength, rightLeanHeight, roofRibDirection);
      
      // Clear selection box when building is rebuilt (items are recreated)
      if (three.selectionBox) {
        three.scene.remove(three.selectionBox);
        three.selectionBox = null;
      }
    }
  }, [width, length, legHeight, roofPitch, roofColor, trimColor, wallColor, wainscotColor, leftEnd, rightEnd, frontSide, backSide, isSingleSlope, sidingOrientation, garageDoors, walkDoors, windows, enclosedLength, enclosedSide, leftLean, rightLean, buildingStyle, leftLeanWidth, leftLeanLength, leftLeanHeight, rightLeanWidth, rightLeanLength, rightLeanHeight, buildBuilding, roofRibDirection, doorsWindowsKey]);
  
  // Clear UI selection state when config items change
  useEffect(() => {
    // This runs after doors/windows arrays change - clear selection as items are recreated
    setSelectedItem(null);
    setShowEditPopup(false);
    setShowResizeMenu(false);
    setMeasurements(null);
  }, [garageDoors.length, walkDoors.length, windows.length]);

  // Helper functions for popup actions
  const handleClone = () => {
    if (selectedItem && onCloneItem) {
      onCloneItem(selectedItem.itemType, selectedItem.id);
      setShowEditPopup(false);
      setSelectedItem(null);
    }
  };
  
  const handleDelete = () => {
    if (selectedItem && onDeleteItem) {
      onDeleteItem(selectedItem.itemType, selectedItem.id);
      setShowEditPopup(false);
      setSelectedItem(null);
      setMeasurements(null);
      // Clear selection box
      const three = threeRef.current;
      if (three.selectionBox) {
        three.scene?.remove(three.selectionBox);
        three.selectionBox = null;
      }
    }
  };
  
  const handleResize = (newWidth, newHeight) => {
    if (selectedItem && onChangeItemSize) {
      onChangeItemSize(selectedItem.itemType, selectedItem.id, newWidth, newHeight);
      setShowResizeMenu(false);
      setSelectedItem(prev => prev ? {
        ...prev,
        dimensions: { width: newWidth, height: newHeight }
      } : null);
    }
  };
  
  // Get item type label
  const getItemTypeLabel = (itemType) => {
    switch(itemType) {
      case 'garageDoors': return 'Garage Door';
      case 'walkDoors': return 'Walk-In Door';
      case 'windows': return 'Window';
      default: return 'Item';
    }
  };

  return (
    <div className="w-full h-full relative" data-testid="building-3d-preview">
      <div ref={containerRef} className="w-full h-full" />
      
      {/* Measurements Display - Shows during drag and when item selected */}
      {measurements && selectedItem && (
        <div 
          className="absolute top-16 left-1/2 -translate-x-1/2 bg-blue-600/95 text-white text-sm px-4 py-2 rounded-lg shadow-lg backdrop-blur-sm z-20"
          data-testid="measurements-display"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <span className="text-blue-200">←</span>
              <span className="font-bold">{measurements.leftDist}&apos;</span>
              <span className="text-blue-200">from edge</span>
            </div>
            <div className="h-4 w-px bg-blue-400" />
            <div className="flex items-center gap-1">
              <span className="text-blue-200">{measurements.width}&apos;</span>
              <span className="text-blue-300">×</span>
              <span className="text-blue-200">{measurements.height}&apos;</span>
            </div>
            <div className="h-4 w-px bg-blue-400" />
            <div className="flex items-center gap-1">
              <span className="text-blue-200">from edge</span>
              <span className="font-bold">{measurements.rightDist}&apos;</span>
              <span className="text-blue-200">→</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Popup Menu */}
      {showEditPopup && selectedItem && (
        <div 
          className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
          style={{ 
            left: Math.min(popupPosition.x, window.innerWidth - 200), 
            top: Math.min(popupPosition.y + 10, window.innerHeight - 300)
          }}
          data-testid="item-edit-popup"
        >
          {/* Header */}
          <div className="bg-gray-800 text-white px-4 py-2.5 flex items-center justify-between">
            <span className="font-semibold text-sm">
              {getItemTypeLabel(selectedItem.itemType)}
            </span>
            <button 
              onClick={() => { setShowEditPopup(false); setShowResizeMenu(false); }}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="popup-close-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Info */}
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-600">
            <div className="flex justify-between">
              <span>Wall: <span className="font-medium text-gray-800">{selectedItem.wall}</span></span>
              <span>Position: <span className="font-medium text-gray-800">{selectedItem.position}%</span></span>
            </div>
            {selectedItem.dimensions && (
              <div className="mt-1">
                Size: <span className="font-medium text-gray-800">{selectedItem.dimensions.width}&apos; × {selectedItem.dimensions.height}&apos;</span>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="p-2">
            {/* Clone Button */}
            <button 
              onClick={handleClone}
              disabled={!onCloneItem}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="clone-item-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Duplicate
            </button>
            
            {/* Resize Button */}
            <button 
              onClick={() => setShowResizeMenu(!showResizeMenu)}
              disabled={!onChangeItemSize}
              className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="resize-item-btn"
            >
              <span className="flex items-center gap-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                Resize
              </span>
              <svg className={`w-4 h-4 transition-transform ${showResizeMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Resize Submenu */}
            {showResizeMenu && selectedItem.itemType && doorSizes[selectedItem.itemType] && (
              <div className="ml-6 mt-1 mb-2 space-y-1 max-h-40 overflow-y-auto">
                {doorSizes[selectedItem.itemType].map((size, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleResize(size.width, size.height)}
                    className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${
                      selectedItem.dimensions?.width === size.width && selectedItem.dimensions?.height === size.height
                        ? 'bg-blue-100 text-blue-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    data-testid={`resize-option-${idx}`}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* Delete Button */}
            <button 
              onClick={handleDelete}
              disabled={!onDeleteItem}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-item-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      )}
      
      {/* Building Dimensions */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-2.5 border border-gray-200">
        <div className="flex items-center gap-3 text-sm font-bold text-gray-800">
          <span className="text-red-600">{width}&apos; Wide</span>
          <span className="text-gray-300">×</span>
          <span className="text-red-600">{length}&apos; Long</span>
          <span className="text-gray-300">×</span>
          <span className="text-red-600">{legHeight}&apos; Tall</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg flex gap-3 backdrop-blur-sm">
        <span>🖱 Drag to rotate</span>
        <span>📜 Scroll to zoom</span>
        <span>🚪 Click/drag doors to edit</span>
      </div>
      
      {/* Selected Item Indicator */}
      {selectedItem && !showEditPopup && (
        <div className="absolute top-3 right-3 bg-green-600/90 text-white text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          <span>{getItemTypeLabel(selectedItem.itemType)} selected</span>
        </div>
      )}

      <div className="absolute bottom-4 right-4 flex gap-2">
        <button className="px-4 py-2 bg-white/95 rounded-lg shadow-lg text-sm font-semibold text-gray-700 hover:bg-white border border-gray-200 transition-colors">
          SCENE
        </button>
        <button className="px-4 py-2 bg-white/95 rounded-lg shadow-lg text-sm font-semibold text-gray-700 hover:bg-white border border-gray-200 transition-colors">
          SHARE
        </button>
      </div>
    </div>
  );
};

export default Building3DPreview;
